/**
 * Budget Workbench — the department-sliced HOD-session surface.
 *
 * Pick a Department (Financial) and/or Campus, sit with the department head,
 * and correct the baseline in one place: every seat grouped by role with its
 * funding, actuals and flags; the slice's RF1 lines (linked / unallocated /
 * released); and inline audited actions — Fund/Move a line, Release a line,
 * Retire a vacant seat (budget-release gated), Reallocate a seat's dims.
 * Every action requires a reason and lands on the change ledger, and the
 * bridge bar shows Original imported vs Current-on-seats at all times.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  ArrowRightLeft, Loader2, RefreshCw, ScrollText, Trash2, MapPin, AlertTriangle,
  Inbox, Wallet, GitBranch, Search, ChevronDown, ChevronRight, StickyNote, Undo2,
  Download, FilterX, ListFilter,
} from 'lucide-react';
import {
  budgetService, organizationService, BudgetFilterOptions, WorkbenchPayload, WorkbenchSeat,
  WorkbenchLine, WorkbenchLedgerResponse, WorkbenchLedgerEntry, BudgetChangeRequest,
} from '@/services/api/organizationService';
import { useAuth } from '@/providers/AuthProvider';
import { useConfirm } from '@/hooks/useConfirm';
import { hasRole, hasAnyPermission, BUDGET_ROLES } from '@/auth/permissions';
import {
  FlagFilter, RoleMode, matchesRoles, matchesView, roleOptions, activeFilterLabels,
} from './workbenchFilters';
import { retireSeatWithRequestGate } from './workbenchRetire';

const zar = new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 });
const fmtK = (v: number) => (Math.abs(v) >= 1e6 ? `R${(v / 1e6).toFixed(1)}m` : `R${Math.round(v / 1e3)}k`);

/** Searchable slicer dropdown — replaces the Radix Select for long code lists
 *  (department/campus): mouse-wheel scrolling flew past entries in the HOD
 *  session (Finance, 30 Jul), so type-to-filter beats scrolling ~24 codes. */
const SearchSelect: React.FC<{
  value: string; onChange: (v: string) => void; options: string[];
  placeholder: string; anyLabel: string; width?: string;
}> = ({ value, onChange, options, placeholder, anyLabel, width }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const filtered = options.filter(o => o.toLowerCase().includes(q.trim().toLowerCase()));
  const pick = (v: string) => { onChange(v); setOpen(false); };
  return (
    <div className={cn('relative', width)}>
      <button type="button"
        className="flex h-8 w-full items-center justify-between rounded-md border border-input bg-background px-2 text-xs"
        onClick={() => { setOpen(o => !o); setQ(''); }}>
        <span className={value ? '' : 'text-muted-foreground'}>{value || placeholder}</span>
        <ChevronDown className="h-3.5 w-3.5 opacity-50" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
            <div className="p-1">
              <Input autoFocus className="h-7 text-xs" placeholder="Type to filter…"
                value={q} onChange={e => setQ(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && filtered.length === 1) pick(filtered[0]);
                  if (e.key === 'Escape') setOpen(false);
                }} />
            </div>
            <div className="max-h-60 overflow-y-auto p-1">
              <button type="button"
                className="block w-full rounded px-2 py-1 text-left text-xs text-muted-foreground hover:bg-muted"
                onClick={() => pick('')}>
                {anyLabel}
              </button>
              {filtered.map(o => (
                <button key={o} type="button"
                  className={cn('block w-full rounded px-2 py-1 text-left text-xs hover:bg-muted',
                    o === value && 'font-semibold text-heritage-blue')}
                  onClick={() => pick(o)}>
                  {o}{o === value ? ' ✓' : ''}
                </button>
              ))}
              {!filtered.length && (
                <div className="px-2 py-1.5 text-[11px] text-gray-400">No match</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/** Role include/exclude picker. "Hide ticked roles" drops them from the list;
 *  "Show only ticked roles" narrows the list to them. Counts are seats in the slice. */
const RolePicker: React.FC<{
  options: Array<{ role: string; seats: number }>;
  selected: string[];
  mode: RoleMode;
  onChange: (mode: RoleMode, roles: string[]) => void;
}> = ({ options, selected, mode, onChange }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const shown = options.filter(o => o.role.toLowerCase().includes(q.trim().toLowerCase()));
  const toggle = (role: string) =>
    onChange(mode, selected.includes(role) ? selected.filter(r => r !== role) : [...selected, role]);
  const label = selected.length
    ? `${mode === 'include' ? 'Only' : 'Hiding'} ${selected.length} role${selected.length === 1 ? '' : 's'}`
    : 'Roles: all';
  return (
    <div className="relative">
      <button type="button" onClick={() => { setOpen(o => !o); setQ(''); }}
        title="Choose roles to hide from the list, or the only roles to show"
        className={cn('flex h-7 items-center gap-1 rounded border px-2 text-[11px]',
          selected.length ? 'border-heritage-blue bg-heritage-blue/10 text-heritage-blue' : 'bg-white text-gray-600')}>
        <ListFilter className="h-3.5 w-3.5" /> {label} <ChevronDown className="h-3 w-3 opacity-50" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 w-80 rounded-md border bg-popover p-2 shadow-md">
            <div className="mb-2 flex gap-1 rounded border bg-gray-50 p-0.5 text-[11px]">
              {(['exclude', 'include'] as RoleMode[]).map(m => (
                <button key={m} type="button" onClick={() => onChange(m, selected)}
                  className={cn('flex-1 rounded px-2 py-0.5',
                    mode === m ? 'bg-heritage-blue text-white' : 'text-gray-600 hover:bg-gray-100')}>
                  {m === 'exclude' ? 'Hide ticked roles' : 'Show only ticked roles'}
                </button>
              ))}
            </div>
            <Input autoFocus className="mb-1 h-7 text-xs" placeholder="Type to filter roles…"
              value={q} onChange={e => setQ(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }} />
            <div className="max-h-64 overflow-y-auto">
              {shown.map(o => (
                <label key={o.role}
                  className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs hover:bg-muted">
                  <input type="checkbox" checked={selected.includes(o.role)} onChange={() => toggle(o.role)} />
                  <span className="min-w-0 flex-1 truncate" title={o.role}>{o.role}</span>
                  <span className="text-[10px] text-gray-400">{o.seats}</span>
                </label>
              ))}
              {!shown.length && <div className="px-2 py-1.5 text-[11px] text-gray-400">No match</div>}
            </div>
            <div className="mt-2 flex items-center justify-between border-t pt-2 text-[11px]">
              <button type="button" className="text-gray-500 hover:text-gray-800"
                onClick={() => onChange(mode, Array.from(new Set([...selected, ...shown.map(o => o.role)])))}>
                Tick all shown
              </button>
              <button type="button" className="text-gray-500 hover:text-gray-800" onClick={() => onChange(mode, [])}>
                Clear roles
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const FLAG_META: Record<string, { label: string; cls: string }> = {
  unfunded_staff: { label: 'Unfunded staff', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  funded_vacancy: { label: 'Funded vacancy', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  unbudgeted_vacant: { label: 'Unbudgeted vacant', cls: 'bg-slate-50 text-slate-500 border-slate-200' },
};

/** Searchable single-select for long candidate lists (Fund/Assign pickers).
 *  Structured columns (code | role · person | dims | amount) with truncation so
 *  long titles can never force horizontal overflow; the search haystack covers
 *  PN, name, position/budget code, job title AND dims. */
type PickOption = {
  value: string; code: string; title: string;
  person?: string; pn?: string; dims?: string; amount: string; note?: string;
};
const SearchPick: React.FC<{
  placeholder: string;
  options: PickOption[];
  value: string;
  onChange: (v: string) => void;
}> = ({ placeholder, options, value, onChange }) => {
  const [q, setQ] = useState('');
  const ql = q.trim().toLowerCase();
  const hay = (o: PickOption) =>
    `${o.code} ${o.title} ${o.person || ''} ${o.pn || ''} ${o.dims || ''} ${o.note || ''}`.toLowerCase();
  const chosen = options.find(o => o.value === value);
  const visible = options.filter(o => o.value !== value && (!ql || hay(o).includes(ql)));

  const Row = ({ o, selected }: { o: PickOption; selected?: boolean }) => (
    <div
      className={cn('grid w-full cursor-pointer grid-cols-[86px,minmax(0,1fr),96px,84px] items-center gap-2 px-2 py-1.5 text-xs',
        selected ? 'border-b bg-sky-50' : 'hover:bg-muted/60')}
      onClick={() => onChange(selected ? '' : o.value)}
      title={selected ? 'Click to unselect' : `${o.title}${o.person ? ` · ${o.person}` : ''}`}>
      <span className="truncate font-mono text-[10px] text-gray-600">{o.code}</span>
      <span className="min-w-0 truncate">
        {o.title}
        {o.person && <span className="text-gray-500"> · {o.person}{o.pn ? ` (${o.pn})` : ''}</span>}
      </span>
      <span className="truncate text-[10px] text-gray-500">{o.dims || '—'}</span>
      <span className="whitespace-nowrap text-right tabular-nums">
        {o.amount}
        {selected ? <span className="ml-1 text-[10px] text-sky-700">✓</span>
          : o.note ? <span className="block text-[9px] leading-3 text-gray-400">{o.note}</span> : null}
      </span>
    </div>
  );

  return (
    <div className="overflow-hidden rounded border">
      <Input className="h-8 rounded-none border-0 border-b text-xs" placeholder={placeholder}
        value={q} onChange={e => setQ(e.target.value)} />
      <div className="max-h-60 overflow-y-auto overflow-x-hidden">
        {chosen && <Row o={chosen} selected />}
        {visible.map(o => <Row key={o.value} o={o} />)}
        {!visible.length && !chosen && (
          <div className="px-2 py-3 text-center text-[11px] text-gray-400">No matches — search covers code, role, person, PN and dims</div>
        )}
      </div>
    </div>
  );
};

type ActionState =
  | { kind: 'fund'; seat: WorkbenchSeat }                       // pick a line -> move onto this seat
  | { kind: 'move'; line: WorkbenchLine }                        // pick a seat -> move this line
  | { kind: 'release'; line: WorkbenchLine }
  | { kind: 'retire'; seat: WorkbenchSeat }
  // Only identity is needed, so a mover alert's lightweight previous_seat can
  // raise this as well as a full table row.
  | { kind: 'reinstate'; seat: { fo_position_id: string; title: string } }
  | { kind: 'reallocate'; seat: WorkbenchSeat }
  | { kind: 'note'; seat: WorkbenchSeat }                       // funding-context note (ledger-backed)
  | null;

const BudgetWorkbench: React.FC<{ year: number }> = ({ year }) => {
  const [options, setOptions] = useState<BudgetFilterOptions | null>(null);
  // Slicer survives refresh (HOD sessions refresh often; re-picking the
  // department every time was the complaint).
  const [department, setDepartment] = useState<string>(() => localStorage.getItem('wb.department') || '');
  const [campus, setCampus] = useState<string>(() => localStorage.getItem('wb.campus') || '');
  // CSV, because reviewing Permanent and FTC together while ignoring ICA and
  // Casual is the normal way through a department — two separate passes hide
  // the case that matters most, a PERM vacancy opposite an FTC incumbent.
  const [employment, setEmployment] = useState<string>(() => localStorage.getItem('wb.employment') || '');
  const [faculty, setFaculty] = useState<string>(() => localStorage.getItem('wb.faculty') || '');
  const employmentSel = employment ? employment.split(',').filter(Boolean) : [];
  const toggleEmployment = (v: string) =>
    setEmployment(
      (employmentSel.includes(v) ? employmentSel.filter(x => x !== v) : [...employmentSel, v]).join(','),
    );
  useEffect(() => { localStorage.setItem('wb.department', department); }, [department]);
  useEffect(() => { localStorage.setItem('wb.campus', campus); }, [campus]);
  useEffect(() => { localStorage.setItem('wb.employment', employment); }, [employment]);
  useEffect(() => { localStorage.setItem('wb.faculty', faculty); }, [faculty]);
  // Retired seats keep their actuals — this toggle keeps them VISIBLE, so
  // retiring an unbudgeted vacancy with part-year spend never hides history.
  // Screen-share privacy: HOD sessions are projected, so individual salary
  // figures (seat budget, YTD CTC, line amounts) can be masked. Persisted,
  // because forgetting to re-tick it before sharing is the whole risk.
  const [hideSalaries, setHideSalaries] = useState<boolean>(() => localStorage.getItem('wb.hideSalaries') === '1');
  useEffect(() => { localStorage.setItem('wb.hideSalaries', hideSalaries ? '1' : '0'); }, [hideSalaries]);
  const [includeAbolished, setIncludeAbolished] = useState<boolean>(() => localStorage.getItem('wb.abolished') === '1');
  useEffect(() => { localStorage.setItem('wb.abolished', includeAbolished ? '1' : '0'); }, [includeAbolished]);
  const [data, setData] = useState<WorkbenchPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<ActionState>(null);
  const [reason, setReason] = useState('');
  const [target, setTarget] = useState('');
  // Reallocate dialog dims — seeded with the seat's current values on open.
  const [reallocCampus, setReallocCampus] = useState('');
  const [reallocDept, setReallocDept] = useState('');
  const [reallocFac, setReallocFac] = useState('');
  // Moved-people panel starts collapsed (it can run to 14+ rows).
  const [moversOpen, setMoversOpen] = useState(false);
  // Grade-band / job-coding panel — same collapsed-by-default treatment.
  const [bandOpen, setBandOpen] = useState(false);

  // "Request new seat" — a REVIEW panel (what will be created) with overrides,
  // because the line's budgeted title/dims are defaults, not the law: a
  // re-titled role often has no job of its own yet (Vacancy 443 'Digital
  // Marketing Strategist' vs job RM-0066 'Digital Sales and Marketing Manager').
  const [seatReqOpen, setSeatReqOpen] = useState(false);
  const [seatReqTitle, setSeatReqTitle] = useState('');
  const [seatReqCampus, setSeatReqCampus] = useState('');
  const [seatReqDept, setSeatReqDept] = useState('');
  const [seatReqFaculty, setSeatReqFaculty] = useState('');
  const [seatReqJobChoice, setSeatReqJobChoice] = useState('');
  const [seatReqJobLabel, setSeatReqJobLabel] = useState('');
  const [jobQ, setJobQ] = useState('');
  const [jobResults, setJobResults] = useState<Array<{ job_id: string; title: string; seats: number }>>([]);
  const [jobBusy, setJobBusy] = useState(false);

  const openSeatRequest = (line: WorkbenchLine) => {
    setSeatReqOpen(true);
    setSeatReqTitle(line.title || '');
    setSeatReqCampus(line.campus || '');
    setSeatReqDept(line.department || '');
    setSeatReqFaculty('NA');
    setSeatReqJobChoice(''); setSeatReqJobLabel('');
    setJobQ(line.title || ''); searchJobs(line.title || '');
  };

  const searchJobs = async (q: string) => {
    setJobQ(q);
    setJobBusy(true);
    try {
      const r = await budgetService.workbenchJobSearch(q);
      setJobResults(r.jobs);
    } catch { setJobResults([]); } finally { setJobBusy(false); }
  };

  const requestSeat = async () => {
    if (action?.kind !== 'move') return;
    setBusy(true);
    try {
      const r = await budgetService.workbenchRequestSeat(action.line.item_id, reason, {
        jobId: seatReqJobChoice || undefined,
        title: seatReqTitle || undefined,
        campus: seatReqCampus || undefined,
        department: seatReqDept || undefined,
        faculty: seatReqFaculty || undefined,
      });
      setFlash({ ok: true, text: `Request ${r.request_number} raised — ${r.title} (${r.job_id}${r.position_type ? ` · ${r.position_type}` : ''}). Approve it on the Requests tab; the seat is born funded by ${action.line.code}.` });
      setSeatReqOpen(false);
      closeDialog(); load(); loadLedger();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === 'job_ambiguous' || detail?.error === 'job_unresolved') {
        setFlash({ ok: false, text: 'Pick the F&O job the new seat should use (search below).' });
      } else {
        setFlash({ ok: false, text: apiError(e, 'Request failed') });
      }
    } finally {
      setBusy(false);
    }
  };

  // Moved-people panel: inline "Mark reviewed" ack (ledgered with reason).
  const [ackFor, setAckFor] = useState<{ item_id: number; seat: string; type: string } | null>(null);
  const [ackReason, setAckReason] = useState('');
  const [ackBusy, setAckBusy] = useState(false);

  const confirmAck = async () => {
    if (!ackFor || ackReason.trim().length < 5) return;
    setAckBusy(true);
    try {
      await budgetService.ackMoverAlert(ackFor.item_id, ackFor.seat, ackReason.trim(), ackFor.type);
      setFlash({ ok: true, text: 'Alert marked reviewed — recorded on the ledger.' });
      setAckFor(null); setAckReason('');
      load(); loadLedger();
    } catch (e: any) {
      setFlash({ ok: false, text: apiError(e, 'Failed to record review') });
    } finally {
      setAckBusy(false);
    }
  };

  // Cross-slice destination search in the Assign dialog — the line followed
  // its person out of the slice, so the slice payload can't offer the target.
  const [xsQ, setXsQ] = useState('');
  const [xsResults, setXsResults] = useState<Array<{
    fo_position_id: string; title: string; campus?: string; department?: string;
    seat_type?: string | null; incumbent?: string | null; pn?: string | null;
    budgeted_annual: number;
  }>>([]);
  const [xsBusy, setXsBusy] = useState(false);

  const searchSeatsGlobal = async (q: string) => {
    setXsQ(q);
    if (q.trim().length < 2) { setXsResults([]); return; }
    setXsBusy(true);
    try {
      const res = await budgetService.workbenchSeatSearch(q.trim());
      setXsResults(res.seats);
    } catch {
      setXsResults([]);
    } finally {
      setXsBusy(false);
    }
  };

  // Cross-type override (the CHRO's rule, HR 5 Aug): allowed as a DELIBERATE
  // act with a justification — surfaces hidden candidates + passes the API flag.
  const [allowCrossType, setAllowCrossType] = useState(false);
  // Deliberate multi-line seat. The server refuses a funded destination without
  // it, because Revert/Undo pre-set the target and bypass the picker's guard.
  const [allowStacking, setAllowStacking] = useState(false);
  // Split-line panel: parts must sum EXACTLY to the parent.
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitParts, setSplitParts] = useState<Array<{ annual: string; title: string }>>([]);
  // Funding-note ref line (optional) for the 'note' action.
  const [noteRef, setNoteRef] = useState('');
  // Swap mode: exchange this line with the one on a FUNDED seat (the
  // deadlock the ordinary picker can't serve).
  const [swapMode, setSwapMode] = useState(false);
  const [swapTarget, setSwapTarget] = useState<number | null>(null);
  // Structure dialog: reassign the seat to a different Role Master JOB
  // (the two junior-band HRBP vacancies belong under RM-0131). '' = keep.
  const [reallocJob, setReallocJob] = useState('');
  const [reallocJobLabel, setReallocJobLabel] = useState('');

  // Reporting-line change inside the Structure dialog: '' = keep current.
  const [reallocParent, setReallocParent] = useState('');
  const [reallocParentLabel, setReallocParentLabel] = useState('');
  const [parentQuery, setParentQuery] = useState('');
  const [parentResults, setParentResults] = useState<Array<{ code: string; title: string; who: string }>>([]);
  const [parentSearching, setParentSearching] = useState(false);

  // Same parent search the /organization page uses: live seats only, never self.
  const searchParents = async (q: string, selfCode: string) => {
    if (q.trim().length < 2) { setParentResults([]); return; }
    setParentSearching(true);
    try {
      const positions = await organizationService.listPositions({
        query: q.trim(), limit: 20, status: ['ACTIVE', 'VACANT'],
      });
      setParentResults(positions
        .filter((p: any) => p.position_code !== selfCode)
        .map((p: any) => ({
          code: p.position_code,
          title: p.title || '—',
          who: p.incumbent_name || '(vacant)',
        })));
    } catch {
      setParentResults([]);
    } finally {
      setParentSearching(false);
    }
  };
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<{ ok: boolean; text: string } | null>(null);
  const [showLedger, setShowLedger] = useState(true);
  const [tableSearch, setTableSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [flagFilter, setFlagFilter] = useState<FlagFilter>('all');
  // Role include/exclude, remembered per slice: a selection made on SELLSA must
  // not silently hide rows the next time ITENDU is opened. Written on change,
  // not by an effect, so loading one slice's choice never overwrites another's.
  const roleKey = `wb.roles.${department || campus || 'any'}`;
  const [roleMode, setRoleMode] = useState<RoleMode>('exclude');
  const [roleSel, setRoleSel] = useState<string[]>([]);
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(roleKey) || 'null');
      setRoleMode(saved?.mode === 'include' ? 'include' : 'exclude');
      setRoleSel(Array.isArray(saved?.roles) ? saved.roles.filter((r: unknown) => typeof r === 'string') : []);
    } catch {
      setRoleMode('exclude'); setRoleSel([]);
    }
  }, [roleKey]);
  const updateRoles = useCallback((mode: RoleMode, roles: string[]) => {
    setRoleMode(mode); setRoleSel(roles);
    try { localStorage.setItem(roleKey, JSON.stringify({ mode, roles })); } catch { /* storage blocked */ }
  }, [roleKey]);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Ledger panel: global by default with per-slice narrowing + filters.
  const [ledgerScope, setLedgerScope] = useState<'slice' | 'all'>('slice');
  const [ledgerAction, setLedgerAction] = useState('');
  const [ledgerSearch, setLedgerSearch] = useState('');
  const [ledger, setLedger] = useState<WorkbenchLedgerResponse | null>(null);

  useEffect(() => {
    budgetService.getBudgetFilterOptions().then(setOptions).catch(() => {});
  }, []);

  // Money renderer honouring the privacy toggle (aggregate bridge excluded).
  // API errors carry structured guidance ({error, detail, subordinates, note});
  // render the human parts, never the object.
  const apiError = (e: any, fallback: string): string => {
    const d = e?.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (!d) return e?.message || fallback;
    return [d.detail || fallback, d.note].filter(Boolean).join(' ');
  };

  const money = useCallback((v: number) => (hideSalaries ? '•••' : fmtK(v)), [hideSalaries]);

  const load = useCallback(() => {
    if (!department && !campus) { setData(null); return; }
    setLoading(true);
    setError(null);
    budgetService.getWorkbench({
      year, department: department || undefined, campus: campus || undefined,
      employment: employment || undefined, faculty: faculty || undefined,
      include_abolished: includeAbolished,
    })
      .then(setData)
      .catch(() => setError('Failed to load the slice'))
      .finally(() => setLoading(false));
  }, [year, department, campus, faculty, employment, includeAbolished]);
  useEffect(load, [load]);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 5000);
    return () => clearTimeout(t);
  }, [flash]);

  // Change requests for this slice. Scoped callers get only their own
  // departments' from the server, so no client-side filtering is needed.
  const [requests, setRequests] = useState<BudgetChangeRequest[]>([]);
  const [reqOpen, setReqOpen] = useState(false);
  const [reqBusy, setReqBusy] = useState<number | null>(null);
  const [declineFor, setDeclineFor] = useState<number | null>(null);
  const [declineNote, setDeclineNote] = useState('');

  const loadRequests = useCallback(() => {
    budgetService.workbenchListChangeRequests(year, department || undefined)
      .then(r => setRequests(r.requests || []))
      .catch(() => setRequests([]));
  }, [year, department]);
  useEffect(loadRequests, [loadRequests]);

  const openRequests = requests.filter(r => r.status === 'open');

  const resolveRequest = async (id: number, status: 'accepted' | 'declined', note?: string) => {
    setReqBusy(id);
    try {
      const r = await budgetService.workbenchResolveChangeRequest(id, status, note);
      // Accepting records a decision and hands back a pre-fill. It does NOT
      // perform the change — the operator still opens the action and confirms,
      // which is the step that writes the ledger.
      if (status === 'accepted' && r.prefill) {
        setFlash({
          ok: true,
          text: `Request #${id} accepted — now make the change on `
            + `${r.prefill.fo_position_id || 'the seat'}. Nothing has moved yet.`,
        });
      } else {
        setFlash({ ok: true, text: `Request #${id} ${status}.` });
      }
      setDeclineFor(null); setDeclineNote('');
      loadRequests();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setFlash({ ok: false, text: typeof d === 'string' ? d : (e?.message || 'Could not resolve the request') });
    } finally {
      setReqBusy(null);
    }
  };

  const loadLedger = useCallback(() => {
    budgetService.getWorkbenchLedger({
      year,
      department: ledgerScope === 'slice' ? (department || undefined) : undefined,
      campus: ledgerScope === 'slice' ? (campus || undefined) : undefined,
      action: ledgerAction || undefined,
      q: ledgerSearch.trim() || undefined,
    }).then(setLedger).catch(() => setLedger(null));
  }, [year, ledgerScope, department, campus, ledgerAction, ledgerSearch]);
  useEffect(loadLedger, [loadLedger]);

  // Click-to-navigate: jump to the entry's slice and surface the affected
  // seat/line via the table filter.
  // Action-aware transition line — '005441 -> 005441' told the reader nothing
  // for same-seat mutations; the real change lives in before/after state.
  const entryTransition = (e: WorkbenchLedgerEntry): string | null => {
    const b = e.before_state || {}; const a = e.after_state || {};
    const seat = e.from_fo_position_id || e.to_fo_position_id || '';
    switch (e.action) {
      case 'reassign_job':
        return `${seat} · job ${b.job || '—'} → ${a.job || '—'}${a.title ? ` (${a.title})` : ''}`;
      case 'reallocate_seat':
        return `${seat} · ${[b.campus, b.department].filter(Boolean).join('/') || '—'} → ${[a.campus, a.department].filter(Boolean).join('/') || '—'}`;
      case 'set_reporting_line':
        return `${seat} · ↳${b.reports_to || '—'} → ↳${a.reports_to || '—'}`;
      case 'split_line':
        return `${e.rf1_code} → ${(a.children || []).map((c: any) => c.code).join(' + ')}`;
      case 'funding_note':
        return e.to_fo_position_id ? `${e.to_fo_position_id}${a.ref_line ? ` · ref ${a.ref_line}` : ''}` : null;
      default:
        if (e.from_fo_position_id && e.to_fo_position_id && e.from_fo_position_id !== e.to_fo_position_id)
          return `${e.from_fo_position_id} → ${e.to_fo_position_id}`;
        return e.to_fo_position_id || e.from_fo_position_id || null;
    }
  };

  // One-click reverse of a move_line entry: the ledger knows where the line
  // came FROM, so undoing a mis-move (New11 instead of New 12, 5 Aug) is just
  // the mirror move — itself ledgered, with the original entry referenced.
  const [undoBusy, setUndoBusy] = useState<number | null>(null);
  const undoMove = async (e: WorkbenchLedgerEntry) => {
    if (!e.item_id || !e.from_fo_position_id) return;
    setUndoBusy(e.id);
    try {
      await budgetService.workbenchMoveLine(e.item_id, e.from_fo_position_id,
        `UNDO of ${e.at?.slice(0, 16)} move (${e.rf1_code}): ${e.reason}`, true);
      setFlash({ ok: true, text: `${e.rf1_code} moved back to ${e.from_fo_position_id}` });
      load(); loadLedger();
    } catch (err: any) {
      setFlash({ ok: false, text: apiError(err, 'Undo failed') });
    } finally {
      setUndoBusy(null);
    }
  };

  const navigateToEntry = (e: { department?: string; campus_code?: string; to_fo_position_id?: string; from_fo_position_id?: string; rf1_code?: string }) => {
    if (e.department) setDepartment(e.department);
    if (e.campus_code) setCampus(e.campus_code);
    setFlagFilter('all');
    setTableSearch(e.to_fo_position_id || e.from_fo_position_id || e.rf1_code || '');
  };

  const seatFlag = (s: WorkbenchSeat): FlagFilter =>
    (s.flag as FlagFilter) || 'funded_filled';

  const roleOpts = useMemo(() => roleOptions(data?.seats ?? []), [data]);
  // Only roles present in this slice count — a remembered role that has since
  // gone must not empty an "only these roles" view.
  const effectiveRoles = useMemo(() => {
    const present = new Set(roleOpts.map(o => o.role));
    return roleSel.filter(r => present.has(r));
  }, [roleOpts, roleSel]);
  const roleScoped = useMemo(
    () => (data?.seats ?? []).filter(s => matchesRoles(s, effectiveRoles, roleMode)),
    [data, effectiveRoles, roleMode]);

  const flagCounts = useMemo(() => {
    const c: Record<FlagFilter, number> = {
      all: roleScoped.length, unfunded_staff: 0, funded_vacancy: 0,
      unbudgeted_vacant: 0, funded_filled: 0,
    };
    for (const s of roleScoped) c[seatFlag(s)] += 1;
    return c;
  }, [roleScoped]);

  // Role-grouped seats, after the flag chips, the role picker and the free-text filter
  const groups = useMemo(() => {
    const view = { flag: flagFilter, query: tableSearch, roles: effectiveRoles, roleMode };
    const g = new Map<string, WorkbenchSeat[]>();
    for (const s of data?.seats ?? []) {
      if (!matchesView(s, view)) continue;
      const k = s.title || '(untitled)';
      if (!g.has(k)) g.set(k, []);
      g.get(k)!.push(s);
    }
    return [...g.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [data, tableSearch, flagFilter, effectiveRoles, roleMode]);

  const shownSeats = groups.reduce((n, [, ss]) => n + ss.length, 0);

  // Everything except the Department choice counts as a filter. With no
  // department chosen the campus IS the slice, so it stays.
  const filterLabels = activeFilterLabels({
    campus: department ? campus : '', faculty, employment, includeAbolished,
    query: tableSearch, flag: flagFilter, roles: effectiveRoles,
  }, roleMode);
  const clearAllFilters = () => {
    if (department) setCampus('');
    setFaculty(''); setEmployment(''); setIncludeAbolished(false);
    setTableSearch(''); setFlagFilter('all'); updateRoles('exclude', []);
  };

  const exportPack = async (filtered: boolean) => {
    setExportOpen(false);
    setExporting(true);
    try {
      const blob = await budgetService.exportWorkbench({
        year, department: department || undefined, campus: campus || undefined,
        faculty: faculty || undefined, employment: employment || undefined,
        include_abolished: includeAbolished, filtered, flag: flagFilter,
        q: tableSearch.trim() || undefined, roles: effectiveRoles, role_mode: roleMode,
        hide_salaries: hideSalaries,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${department || campus || 'slice'}_Budget_Review${filtered ? '_filtered' : ''}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setFlash({ ok: false, text: apiError(err, 'Export failed') });
    } finally {
      setExporting(false);
    }
  };

  const toggleGroup = (title: string) => setCollapsed(prev => {
    const n = new Set(prev);
    if (n.has(title)) n.delete(title); else n.add(title);
    return n;
  });

  // Adjustment lines (removals/savings/specials) are ENVELOPE entries — they can
  // never fund a seat, so they don't belong in the unallocated panel (the
  // "Remove · Executive in the office of the CEO · −R879k" confusion).
  const ADJ_SOURCES = ['remove', 'savings', 'special'];
  const unallocated = useMemo(() => (data?.lines ?? []).filter(
    l => l.state === 'unallocated' && !ADJ_SOURCES.includes(l.source || '')), [data]);
  const adjustments = useMemo(() => (data?.lines ?? []).filter(
    l => ADJ_SOURCES.includes(l.source || '')), [data]);
  const released = useMemo(() => (data?.lines ?? []).filter(
    l => l.state === 'released' && !ADJ_SOURCES.includes(l.source || '')), [data]);

  // Candidate lines for funding a seat: unallocated first, then lines linked to
  // funded VACANT seats in the slice (the stale-vacancy relink case).
  // BUDGET IS PER POSITION TYPE — a PERM line must never fund an ICA seat, so
  // lines of a conflicting type are excluded (unknown on either side is fine).
  const fundCandidates = useMemo(() => {
    if (!data || action?.kind !== 'fund') return [];
    const seatType = action.seat.seat_type || action.seat.employment || null;
    const vacantFunded = new Set(
      data.seats.filter(s => s.occupancy === 'vacant' && s.budgeted_annual > 0).map(s => s.fo_position_id));
    const linkedToVacant = data.lines.filter(l => l.state === 'linked' && l.linked_fo && vacantFunded.has(l.linked_fo));
    return [...unallocated, ...linkedToVacant]
      .filter(l => allowCrossType
        || !(seatType && l.employment_type && l.employment_type !== seatType));
  }, [data, action, unallocated, allowCrossType]);
  const fundBlockedByType = useMemo(() => {
    if (!data || action?.kind !== 'fund') return 0;
    const seatType = action.seat.seat_type || action.seat.employment || null;
    if (!seatType) return 0;
    const vacantFunded = new Set(
      data.seats.filter(s => s.occupancy === 'vacant' && s.budgeted_annual > 0).map(s => s.fo_position_id));
    return [...unallocated, ...data.lines.filter(l => l.state === 'linked' && l.linked_fo && vacantFunded.has(l.linked_fo))]
      .filter(l => l.employment_type && l.employment_type !== seatType).length;
  }, [data, action, unallocated]);

  // Candidate seats for ASSIGNING a line: only seats that NEED funding —
  // unfunded staff first (they are the point of the exercise), then
  // unbudgeted vacant seats. Funded seats (filled or vacant) are excluded:
  // assigning a second line there would double-fund; dual allocations stay a
  // deliberate Fund-dialog / Finance decision, not a default surface.
  // Within each tier, seats whose title matches the line's title sort first.
  const moveCandidates = useMemo(() => {
    if (!data || action?.kind !== 'move') return [];
    const lineTitle = (action.line.title || '').trim().toLowerCase();
    const lineType = action.line.employment_type || null;
    const tier = (s: WorkbenchSeat) => {
      const titleHit = lineTitle && s.title.trim().toLowerCase() === lineTitle ? 0 : 1;
      const base = s.flag === 'unfunded_staff' ? 0 : s.flag === 'unbudgeted_vacant' ? 2 : 9;
      return base + titleHit;
    };
    return data.seats
      .filter(s => s.budgeted_annual === 0)
      // Budget is per position type — exclude seats whose type conflicts.
      .filter(s => {
        const st = s.seat_type || s.employment || null;
        return allowCrossType || !(lineType && st && st !== lineType);
      })
      .sort((a, b) => tier(a) - tier(b) || a.title.localeCompare(b.title));
  }, [data, action, allowCrossType]);
  // Funded seats whose line could trade places with the one being assigned.
  const swapCandidates = useMemo(() => {
    if (!data || action?.kind !== 'move') return [];
    return data.seats
      .filter(s => s.budgeted_annual > 0 && s.lines.length === 1
        && s.fo_position_id !== action.line.linked_fo)
      .map(s => ({ seat: s, line: s.lines[0] }));
  }, [data, action]);

  const moveBlockedByType = useMemo(() => {
    if (!data || action?.kind !== 'move') return 0;
    const lineType = action.line.employment_type || null;
    if (!lineType) return 0;
    return data.seats.filter(s => s.budgeted_annual === 0
      && (s.seat_type || s.employment) && (s.seat_type || s.employment) !== lineType).length;
  }, [data, action]);

  const closeDialog = () => {
    setAllowStacking(false);
    setAction(null); setReason(''); setTarget('');
    setXsQ(''); setXsResults([]);
    setSeatReqOpen(false); setSeatReqJobChoice(''); setSeatReqJobLabel('');
    setJobQ(''); setJobResults([]);
    setAllowCrossType(false); setSplitOpen(false); setSplitParts([]); setNoteRef('');
    setReallocJob(''); setReallocJobLabel('');
    setSwapMode(false); setSwapTarget(null);
  };

  // A department head with budget:read and no write grant sees this whole
  // screen, scoped to their departments, but may not change anything. Rather
  // than hide the actions - which would leave them looking at a problem with
  // no way to raise it - the same buttons and the same dialog file a REQUEST.
  const { user: wbUser } = useAuth();
  const { confirm: showConfirm } = useConfirm();
  const readOnly =
    !hasRole(wbUser, BUDGET_ROLES) && !hasAnyPermission(wbUser, ['budget:write']);

  // Workbench action kind -> request type. Kinds absent here have no
  // server-side request type and fall back to a free-text 'other'.
  const REQUEST_TYPE_FOR: Record<string, string> = {
    release: 'release_line',
    retire: 'retire_seat',
    move: 'move_line',
    fund: 'move_line',
    reallocate: 'reallocate_seat',
    request_seat: 'request_seat',
  };

  const run = async () => {
    if (!action) return;
    setBusy(true);
    try {
      // Read-only: record what they want and stop. Nothing below this line
      // runs, so no mutation and no ledger entry can happen by accident.
      if (readOnly) {
        const seatId =
          (action as any).seat?.fo_position_id ||
          (action as any).line?.on_seat ||
          undefined;
        const r = await budgetService.workbenchCreateChangeRequest({
          year,
          request_type: (REQUEST_TYPE_FOR[action.kind] || 'other') as any,
          fo_position_id: seatId,
          detail: reason,
        });
        setFlash({
          ok: true,
          text: `Request #${r.id} sent to Finance — nothing has changed yet.`,
        });
        closeDialog();
        loadRequests();
        return;
      }

      if (action.kind === 'fund') {
        const itemId = Number(target);
        if (!itemId) throw new Error('Pick the budget line to move onto this seat');
        await budgetService.workbenchMoveLine(itemId, action.seat.fo_position_id, reason, allowCrossType, allowStacking);
        setFlash({ ok: true, text: `Line moved onto ${action.seat.fo_position_id}${allowCrossType ? " (cross-type override, ledgered)" : ""}${allowStacking ? " (stacked — multi-line seat, ledgered)" : ""}` });
      } else if (action.kind === 'move' && swapMode) {
        if (!swapTarget) throw new Error('Pick the line to swap with');
        const r = await budgetService.workbenchSwapLines(action.line.item_id, swapTarget, reason, allowCrossType);
        setFlash({ ok: true, text: `Swapped: ${r.a.code} → ${r.a.now_on}, ${r.b.code} → ${r.b.now_on}` });
      } else if (action.kind === 'move') {
        if (!target) throw new Error('Pick the destination seat');
        await budgetService.workbenchMoveLine(action.line.item_id, target, reason, allowCrossType, allowStacking);
        setFlash({ ok: true, text: `${action.line.code} moved to ${target}${allowCrossType ? " (cross-type override, ledgered)" : ""}` });
      } else if (action.kind === 'note') {
        await budgetService.workbenchSeatNote(action.seat.fo_position_id, reason, noteRef || undefined);
        setFlash({ ok: true, text: `Funding note recorded on ${action.seat.fo_position_id}` });
      } else if (action.kind === 'release') {
        await budgetService.workbenchReleaseLine(action.line.item_id, reason);
        setFlash({ ok: true, text: `${action.line.code} released (${money(action.line.annual)})` });
      } else if (action.kind === 'retire') {
        const fo = action.seat.fo_position_id;
        const funded = action.seat.budgeted_annual > 0;
        // A seat a position request is still waiting to fill retires only on a
        // deliberate yes; the server records the released request in the ledger.
        const out = await retireSeatWithRequestGate(
          releaseRequest => budgetService.workbenchRetireSeat(fo, reason, funded, releaseRequest),
          (detail, requestNumber) => showConfirm(detail, `Release position request ${requestNumber}?`,
            { confirmText: 'Release the request and retire', type: 'danger' }),
        );
        if (!out.retired) {
          setFlash({ ok: true, text: `${fo} kept: position request ${out.keptFor} still needs it.` });
          closeDialog();
          return;
        }
        setFlash({ ok: true, text: `${fo} retired${funded ? ' (budget released)' : ''}`
          + `${out.releasedRequest ? ` (released from position request ${out.releasedRequest})` : ''}` });
      } else if (action.kind === 'reinstate') {
        const r = await budgetService.workbenchReinstateSeat(action.seat.fo_position_id, reason);
        setFlash({
          ok: true,
          text: `${action.seat.fo_position_id} is live again as a vacant seat`
            + `${r.fo_reinstated === false ? ' (platform only — it has no F&O position)' : ''}`
            + ' — set its job, dims and reporting line in Structure, then move a line onto it.',
        });
      } else if (action.kind === 'reallocate') {
        const s = action.seat;
        const dimsChanged = reallocCampus !== (s.campus || '') || reallocDept !== (s.department || '')
          || reallocFac !== (s.faculty || 'NA');
        const parentChanged = !!reallocParent && reallocParent !== (s.reports_to || '');
        const jobChanged = !!reallocJob && reallocJob !== (s.fo_job_id || '');
        if (!dimsChanged && !parentChanged && !jobChanged) throw new Error('Change at least one dimension, the reporting line, or the job');
        const done: string[] = [];
        if (dimsChanged) {
          await budgetService.workbenchReallocateSeat(s.fo_position_id,
            { campus: reallocCampus || undefined, department: reallocDept || undefined, faculty: reallocFac || undefined }, reason);
          done.push(`dims → ${reallocCampus}/${reallocDept}/${reallocFac}`);
        }
        if (jobChanged) {
          const r = await budgetService.workbenchReassignSeatJob(s.fo_position_id, reallocJob, reason);
          done.push(`job → ${r.job} (${r.title})`);
        }
        if (parentChanged) {
          // Same dispatch as /organization: a VACANT seat is a position-anchored
          // F&O Line-edge write; a FILLED seat must run the employment saga so
          // the incumbent's Sage REPORTSTOEMP follows the F&O edge.
          if (s.occupancy === 'vacant') {
            const res = await organizationService.setPositionReportsTo(s.fo_position_id, reallocParent);
            if (!res.ok) throw new Error(res.error || 'F&O reporting-line write failed');
          } else {
            const { job_id } = await organizationService.bulkSetReportsTo([s.fo_position_id], reallocParent);
            const t0 = Date.now();
            for (;;) {
              await new Promise(r => setTimeout(r, 2500));
              const job = await organizationService.getBulkReportsToJob(job_id);
              if (job.status === 'completed') {
                const item = job.results[0];
                if (item?.status === 'failed') throw new Error(item.error || 'Reporting-line saga failed');
                break;
              }
              if (Date.now() - t0 > 150_000) {
                throw new Error('The reporting-line change is still running (payroll saga) — '
                  + 'verify on the org page in a minute, then re-confirm here so it gets ledgered.');
              }
            }
          }
          // F&O write confirmed — mirror locally + write the audit ledger row.
          await budgetService.workbenchLogReportingLine(s.fo_position_id, reallocParent, reason);
          done.push(`reports to → ${reallocParent}${reallocParentLabel ? ` (${reallocParentLabel})` : ''}`);
        }
        setFlash({ ok: true, text: `${s.fo_position_id}: ${done.join(' · ')}` });
      }
      closeDialog();
      load();
      loadLedger();
    } catch (e: any) {
      setFlash({ ok: false, text: apiError(e, 'Action failed') });
    } finally {
      setBusy(false);
    }
  };

  const b = data?.bridge;

  return (
    <div className="space-y-3">
      {/* Slicer */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-2 p-3">
          <span className="text-xs font-semibold text-muted-foreground">Slice</span>
          <SearchSelect value={department} onChange={setDepartment} width="w-56"
            options={options?.department ?? []}
            placeholder="Department (Financial)" anyLabel="Department: any" />
          <SearchSelect value={campus} onChange={setCampus} width="w-44"
            options={options?.campus ?? []}
            placeholder="Campus" anyLabel="Campus: any" />
          <SearchSelect value={faculty} onChange={setFaculty} width="w-40"
            options={options?.faculty ?? []}
            placeholder="Faculty" anyLabel="Faculty: any" />
          {/* Position type is a multi-select rather than a dropdown: a review
              pass is almost always "Permanent AND FTC, ignoring ICA and Casual",
              and forcing that into two passes hides a PERM vacancy sitting
              opposite an FTC incumbent — the mismatch worth finding. */}
          <div className="flex items-center gap-1 rounded border bg-white px-1.5 py-1"
            title="Filled seats filter on the worker's category; vacant seats classify by their RF1 line's payroll type. Pick more than one to review them together.">
            <span className="text-[10px] text-muted-foreground">Type</span>
            {(options?.employment ?? []).filter(e => e !== 'Vacant').map(e => {
              const on = employmentSel.includes(e);
              return (
                <button key={e} onClick={() => toggleEmployment(e)}
                  className={cn('rounded px-1.5 py-0.5 text-[11px] transition-colors',
                    on ? 'bg-heritage-blue text-white' : 'text-gray-600 hover:bg-gray-100')}>
                  {e}
                </button>
              );
            })}
            {employmentSel.length > 0 && (
              <button onClick={() => setEmployment('')}
                className="ml-0.5 text-[11px] text-gray-400 hover:text-gray-600" title="Clear">
                ×
              </button>
            )}
          </div>
          <label className="flex cursor-pointer items-center gap-1 text-[11px] text-gray-500"
            title="Retired seats keep their actuals — show them greyed so part-year spend on abolished seats stays visible">
            <input type="checkbox" checked={includeAbolished} onChange={e => setIncludeAbolished(e.target.checked)} />
            Show abolished
          </label>
          <label className="flex cursor-pointer items-center gap-1 text-[11px] text-gray-500"
            title="Screen-sharing? Mask every individual salary figure (seat budget, YTD CTC, line amounts). Slice totals stay visible.">
            <input type="checkbox" checked={hideSalaries} onChange={e => setHideSalaries(e.target.checked)} />
            Hide salaries
          </label>
          {filterLabels.length > 0 && (
            <Button variant="outline" size="sm" onClick={clearAllFilters}
              title={`Clears ${filterLabels.join(' · ')}. The department stays selected.`}>
              <FilterX className="mr-1 h-3.5 w-3.5" /> Clear all filters ({filterLabels.length})
            </Button>
          )}
          <div className="relative">
            <Button variant="outline" size="sm" disabled={!data || exporting}
              onClick={() => setExportOpen(o => !o)}
              title="Download this slice as a review pack: priority, issue, Decision dropdown and Notes">
              {exporting
                ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                : <Download className="mr-1 h-3.5 w-3.5" />}
              Export <ChevronDown className="ml-1 h-3 w-3 opacity-50" />
            </Button>
            {exportOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setExportOpen(false)} />
                <div className="absolute right-0 z-50 mt-1 w-80 rounded-md border bg-popover p-1 text-xs shadow-md">
                  <button type="button" className="block w-full rounded px-2 py-1.5 text-left hover:bg-muted"
                    onClick={() => exportPack(false)}>
                    <div className="font-medium">
                      {department ? `The whole of ${department}` : `The whole of campus ${campus}`}
                    </div>
                    <div className="text-[11px] text-gray-500">Every seat in the slice, with no filters applied</div>
                  </button>
                  <button type="button" disabled={!filterLabels.length}
                    className="block w-full rounded px-2 py-1.5 text-left hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => exportPack(true)}>
                    <div className="font-medium">What is on screen ({shownSeats} seats)</div>
                    <div className="text-[11px] text-gray-500">
                      {filterLabels.length ? filterLabels.join(' · ') : 'No filters active — same as the whole slice'}
                    </div>
                  </button>
                  <div className="px-2 pb-1 pt-1.5 text-[10px] text-gray-400">
                    Same layout as the all-issues review pack.
                    {hideSalaries ? ' Individual salaries are left out, as on screen.' : ''}
                  </div>
                </div>
              </>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading || (!department && !campus)}>
            <RefreshCw className={cn('mr-1 h-3.5 w-3.5', loading && 'animate-spin')} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowLedger(v => !v)}>
            <ScrollText className="mr-1 h-3.5 w-3.5" /> Ledger ({ledger?.total ?? 0}{ledger?.truncated ? '+' : ''})
          </Button>
          <Button variant="outline" size="sm" onClick={() => setReqOpen(v => !v)}
            title="Change requests raised by department heads — asks, not actions">
            <Inbox className="mr-1 h-3.5 w-3.5" /> Requests
            {openRequests.length > 0 && (
              <span className="ml-1 rounded-full bg-amber-500 px-1.5 text-[10px] font-semibold text-white">
                {openRequests.length}
              </span>
            )}
          </Button>
          {/* Bridge bar — the before/after at all times */}
          {b && (
            <div className="ml-auto flex items-center gap-3 text-xs tabular-nums">
              {hideSalaries && <span className="text-[10px] text-gray-400" title="Individual figures are masked; these slice totals are aggregates">salaries hidden ·</span>}
              <span title="Sum of every RF1 line imported for this slice (excl. savings/removals)">Original <b>{fmtK(b.original_imported)}</b></span>
              <span aria-hidden>→</span>
              <span title="Budget currently sitting on this slice's seats">Current <b>{fmtK(b.current_on_seats)}</b></span>
              <span className={cn('rounded-full px-2 py-0.5',
                Math.abs(b.delta_vs_original) < 1 ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-amber-50 text-amber-800')}
                title="Current − Original. Every rand of difference is explained by ledger entries (releases, cross-slice moves, unallocated lines).">
                Δ {fmtK(b.delta_vs_original)}
              </span>
              {b.unallocated > 0 && <span className="text-amber-700" title="Active lines in this slice not linked to any seat">unallocated {fmtK(b.unallocated)}</span>}
              {b.released > 0 && <span className="text-gray-500" title="Lines released from the baseline (see ledger for reasons)">released {fmtK(b.released)}</span>}
            </div>
          )}
        </CardContent>
      </Card>

      {flash && (
        <div className={cn('rounded border px-3 py-2 text-xs',
          flash.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-700')}>
          {flash.text}
        </div>
      )}

      {!department && !campus ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          Pick a <b>Department (Financial)</b> and/or <b>Campus</b> to open a working session —
          the intended flow is one department at a time, together with its head.
        </CardContent></Card>
      ) : loading && !data ? (
        <Card><CardContent className="py-12 text-center text-gray-400">
          <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin" />Loading slice…
        </CardContent></Card>
      ) : error ? (
        <Card><CardContent className="py-10 text-center text-sm text-red-600">{error}</CardContent></Card>
      ) : data && (
        <div className={cn('grid gap-3', showLedger ? 'lg:grid-cols-[1fr,340px]' : '')}>
          <div className="space-y-3">
            {/* Grade band & job coding. Kept OUT of "Moved people": nobody
                moved — the seat and its own job disagree, or the RF1's grade
                numeral points at a different role than the seat is coded to.
                Role matching drops the numeral on purpose (it would otherwise
                fire on every "Campus General Manager 1"), which is exactly why
                a grade-2 line on a grade-1 seat has no other way to surface. */}
            {!!data.band_alerts?.length && (
              <Card>
                <CardContent className="p-3 text-xs">
                  <button type="button"
                    className="flex w-full items-center gap-1.5 font-semibold text-violet-800"
                    onClick={() => setBandOpen(o => !o)}>
                    {bandOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    <GitBranch className="h-3.5 w-3.5" />
                    Grade band &amp; job coding ({data.band_alerts.length})
                    <span className="font-normal text-gray-500">
                      — same title, different grade; or a seat coded to a job it isn&apos;t
                    </span>
                    <span className="ml-auto text-[10px] font-normal text-gray-400">
                      {bandOpen ? 'hide' : 'show'}
                    </span>
                  </button>
                  <div className={cn('mt-1.5 space-y-1', !bandOpen && 'hidden')}>
                    {data.band_alerts.map((a, i) => (
                      <div key={i}
                        className="flex w-full flex-wrap items-center gap-2 rounded border border-violet-100 bg-violet-50/50 px-2 py-1.5">
                        <span className="rounded-full border border-violet-200 bg-white px-1.5 py-0.5 text-[10px] text-violet-700">
                          {a.type === 'grade_band' ? 'Grade band' : 'Seat mis-coded'}
                        </span>
                        <span className="font-mono text-[10px] text-sky-700">{a.code}</span>
                        {a.person && <span>{a.person}</span>}
                        <button type="button"
                          className="font-mono text-[10px] text-heritage-blue hover:underline"
                          title="Filter the table to this seat" onClick={() => setTableSearch(a.seat)}>
                          {a.seat}
                        </button>
                        <span className="tabular-nums">{money(a.annual)}</span>
                        <span className="ml-auto flex items-center gap-1">
                          <Button variant="outline" size="sm" className="h-6 px-1.5 text-[10px]"
                            title="Open Structure to re-point this seat's job"
                            onClick={() => {
                              const s = data.seats.find(x => x.fo_position_id === a.seat);
                              if (!s) return;
                              setAction({ kind: 'reallocate', seat: s });
                              setReason('');
                              setReallocCampus(s.campus || ''); setReallocDept(s.department || '');
                              setReallocFac(s.faculty || 'NA');
                              setReallocParent(''); setReallocParentLabel('');
                              setParentQuery(''); setParentResults([]);
                              setReallocJob(''); setReallocJobLabel('');
                            }}>
                            <MapPin className="mr-0.5 h-3 w-3" /> Structure
                          </Button>
                          <Button variant="outline" size="sm" className="h-6 px-1.5 text-[10px]"
                            title="Accept this configuration — recorded on the audit ledger with your reason"
                            onClick={() => { setAckFor({ item_id: a.item_id, seat: a.seat, type: a.type }); setAckReason(''); }}>
                            Mark reviewed
                          </Button>
                        </span>
                        <span className="min-w-0 flex-1 basis-full text-[11px] text-gray-600">{a.detail}</span>
                        {ackFor?.item_id === a.item_id && ackFor?.seat === a.seat && (
                          <div className="flex basis-full items-center gap-1.5 pt-1">
                            <Input autoFocus className="h-7 flex-1 text-xs" value={ackReason}
                              placeholder="Why is this configuration correct? — goes on the audit ledger"
                              onChange={e => setAckReason(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') confirmAck(); if (e.key === 'Escape') setAckFor(null); }} />
                            <Button size="sm" className="h-7 px-2 text-xs" onClick={confirmAck}
                              disabled={ackBusy || ackReason.trim().length < 5}>
                              {ackBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Confirm'}
                            </Button>
                            <Button variant="outline" size="sm" className="h-7 px-2 text-xs"
                              onClick={() => setAckFor(null)} disabled={ackBusy}>Cancel</Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Moved people — budget that followed a PERSON instead of the role.
                The recurring session pattern (a GLC seat, the Receptionists and two
                others): click an entry to jump to its seat. */}
            {!!data.mover_alerts?.length && (
              <Card>
                <CardContent className="p-3 text-xs">
                  {/* Collapsed by default — 14 alerts pushed the seats table
                      off-screen in the S&M session. */}
                  <button type="button"
                    className="flex w-full items-center gap-1.5 font-semibold text-amber-800"
                    onClick={() => setMoversOpen(o => !o)}>
                    {moversOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Moved people ({data.mover_alerts.length})
                    <span className="font-normal text-gray-500">
                      — budget that followed a person instead of the role
                    </span>
                    <span className="ml-auto text-[10px] font-normal text-gray-400">
                      {moversOpen ? 'hide' : 'show'}
                    </span>
                  </button>
                  <div className={cn('mt-1.5 space-y-1', !moversOpen && 'hidden')}>
                    {data.mover_alerts.map((a, i) => (
                      <div key={i}
                        className="flex w-full flex-wrap items-center gap-2 rounded border border-amber-100 bg-amber-50/50 px-2 py-1.5">
                        <span className={cn('rounded-full border px-1.5 py-0.5 text-[10px]',
                          a.type === 'trailing_vacancy'
                            ? 'border-rose-200 bg-rose-50 text-rose-700'
                            : 'border-amber-200 bg-amber-50 text-amber-700')}>
                          {a.type === 'role_changed' ? 'Role changed'
                            : a.type === 'moved_department' ? 'Moved department'
                            : 'Trailing vacancy'}
                        </span>
                        <span className="font-mono text-[10px] text-gray-600">{a.code}</span>
                        {a.person && <span className="font-medium">{a.person}</span>}
                        <button type="button" className="font-mono text-[10px] text-heritage-blue hover:underline"
                          title="Filter the table to this seat" onClick={() => setTableSearch(a.seat)}>
                          {a.seat}
                        </button>
                        <span className="tabular-nums">{money(a.annual)}</span>
                        <span className="ml-auto flex items-center gap-1">
                          {/* An abolished old seat used to hide Revert with no way
                              forward. Reinstate it, and Revert appears on reload. */}
                          {a.previous_seat?.retired && (
                            <Button variant="outline" size="sm"
                              className="h-6 border-emerald-200 px-1.5 text-[10px] text-emerald-700 hover:bg-emerald-50"
                              title={`Bring ${a.previous_seat.fo_position_id} (${a.previous_seat.title}) back as a vacant seat, so ${a.code} can be moved back onto the role it belongs to`}
                              onClick={() => {
                                if (!a.previous_seat) return;
                                setAction({ kind: 'reinstate', seat: {
                                  fo_position_id: a.previous_seat.fo_position_id,
                                  title: a.previous_seat.title,
                                } });
                                setReason(`Reinstate ${a.previous_seat.fo_position_id} so ${a.code} can return to the role it budgets`);
                              }}>
                              <Undo2 className="mr-0.5 h-3 w-3" /> Reinstate {a.previous_seat.fo_position_id}
                            </Button>
                          )}
                          {a.previous_seat && !a.previous_seat.retired && (
                            <Button variant="outline" size="sm"
                              className={cn('h-6 px-1.5 text-[10px]', a.previous_seat.funded
                                ? 'border-amber-300 text-amber-800 hover:bg-amber-50'
                                : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50')}
                              title={`Move ${a.code} back to ${a.previous_seat.fo_position_id} (${a.previous_seat.title})`
                                + (a.previous_seat.funded
                                  ? ` — WARNING: that seat already carries ${money(a.previous_seat.lane || 0)}; this would stack a second line on it`
                                  : a.previous_seat.held_by
                                    ? ` — now held by ${a.previous_seat.held_by}, so the budget funds them`
                                    : ' — restores it as a funded vacancy')}
                              onClick={() => {
                                const full = data.lines.find(x => x.item_id === a.item_id);
                                if (full && a.previous_seat) {
                                  setAction({ kind: 'move', line: full });
                                  setReason(`Budget follows the role: ${a.code} back to ${a.previous_seat.fo_position_id} (${a.previous_seat.title})`);
                                  setTarget(a.previous_seat.fo_position_id);
                                }
                              }}>
                              ↩ Revert to {a.previous_seat.fo_position_id}
                            </Button>
                          )}
                          <Button variant="outline" size="sm" className="h-6 px-1.5 text-[10px]"
                            title="Open the Assign dialog to move this line to the right seat"
                            onClick={() => {
                              const full = data.lines.find(x => x.item_id === a.item_id);
                              if (full) { setAction({ kind: 'move', line: full }); setReason(''); setTarget(''); }
                            }}>
                            <ArrowRightLeft className="mr-0.5 h-3 w-3" /> Move line
                          </Button>
                          <Button variant="outline" size="sm" className="h-6 px-1.5 text-[10px]"
                            title="Accept this configuration — recorded on the audit ledger with your reason"
                            onClick={() => { setAckFor({ item_id: a.item_id, seat: a.seat, type: a.type }); setAckReason(''); }}>
                            Mark reviewed
                          </Button>
                        </span>
                        <span className="min-w-0 flex-1 basis-full text-[11px] text-gray-600">
                          {a.detail}
                          {a.previous_seat && (
                            <span className="text-gray-500">
                              {' '}Was on <span className="font-mono">{a.previous_seat.fo_position_id}</span>{' '}
                              ({a.previous_seat.title})
                              {a.previous_seat.retired
                                ? ' — that seat is abolished; reinstate it above, then revert.'
                                : a.previous_seat.funded
                                  ? ` — ALREADY funded (${money(a.previous_seat.lane || 0)}${a.previous_seat.held_by ? `, ${a.previous_seat.held_by}` : ''}), so reverting would STACK a second line on it, not fund anyone.`
                                  : a.previous_seat.held_by
                                    ? ` — now held by ${a.previous_seat.held_by} and unfunded, so reverting funds them.`
                                    : ' — now vacant and unfunded, so reverting restores it as a funded vacancy.'}
                            </span>
                          )}
                        </span>
                        {ackFor?.item_id === a.item_id && ackFor?.seat === a.seat && (
                          <div className="flex basis-full items-center gap-1.5 pt-1">
                            <Input autoFocus className="h-7 flex-1 text-xs" value={ackReason}
                              placeholder="Why is this configuration correct? — goes on the audit ledger"
                              onChange={e => setAckReason(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') confirmAck(); if (e.key === 'Escape') setAckFor(null); }} />
                            <Button size="sm" className="h-7 px-2 text-xs" onClick={confirmAck}
                              disabled={ackBusy || ackReason.trim().length < 5}>
                              {ackBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Confirm'}
                            </Button>
                            <Button variant="outline" size="sm" className="h-7 px-2 text-xs"
                              onClick={() => setAckFor(null)} disabled={ackBusy}>Cancel</Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Incoming new-position requests touching this slice — what's about
                to land next to the seats being corrected. */}
            {!!data.incoming_requests?.length && (
              <Card>
                <CardContent className="p-3 text-xs">
                  <div className="mb-1.5 flex items-center gap-1.5 font-semibold text-slate-700">
                    <Inbox className="h-3.5 w-3.5" />
                    Incoming position requests ({data.incoming_requests.length})
                    <span className="font-normal text-gray-500">— pending asks for this slice</span>
                    <a href="/role-master?tab=requests" className="ml-auto text-heritage-blue hover:underline">
                      Open Requests tab →
                    </a>
                  </div>
                  <div className="space-y-1">
                    {data.incoming_requests.map(r => (
                      <div key={`${r.id}-${r.request_number}`}
                        className="flex flex-wrap items-center gap-2 rounded border bg-slate-50/60 px-2 py-1.5">
                        <span className="font-medium">{r.job_title || r.title}</span>
                        <span className="text-gray-500">×{r.count}</span>
                        <span className="text-[10px] text-gray-500">
                          {[r.campus, r.department, r.faculty].filter(Boolean).join('·')}
                        </span>
                        <span className={cn('rounded-full border px-1.5 py-0.5 text-[10px]',
                          r.status === 'approved' ? 'border-blue-200 bg-blue-50 text-blue-700'
                            : 'border-amber-200 bg-amber-50 text-amber-700')}>
                          {r.status === 'approved' ? 'Provisioning…' : 'Pending review'}
                        </span>
                        {r.funding_mode === 'line' ? (() => {
                          // funded_positions is null on v1 requests (one line, one seat);
                          // only a v2 multi-position ask can be partly funded.
                          const partial = r.funded_positions != null && r.funded_positions < (r.count || 1);
                          return (
                          <span className={cn('inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px]',
                            partial ? 'border-amber-200 bg-amber-50 text-amber-800'
                                    : 'border-emerald-200 bg-emerald-50 text-emerald-700')}
                            title={`Funded by ${r.funding_code || '—'}`
                              + (partial ? ` — ${(r.count || 1) - (r.funded_positions || 0)} of ${r.count} position(s) still UNFUNDED` : '')}>
                            <Wallet className="h-3 w-3" />
                            {partial ? `Funded ${r.funded_positions} of ${r.count}` : 'Funded'}
                            {' · '}{r.funding_code || '—'}{' · '}{money(r.funding_annual || 0)}
                          </span>
                          );
                        })() : r.funding_mode === 'unbudgeted' ? (
                          <span className="rounded-full border border-slate-200 bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                            Unbudgeted · signed off
                          </span>
                        ) : (
                          <span className="rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700"
                            title="Approval is blocked until funding is resolved on the Requests tab.">
                            Funding unresolved
                          </span>
                        )}
                        {r.requested_by && <span className="text-[10px] text-gray-400">by {r.requested_by}</span>}
                        <span className="ml-auto font-mono text-[10px] text-gray-400">{r.request_number}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Seats, grouped by role */}
            <Card>
              <CardContent className="p-0">
                <div className="flex flex-wrap items-center gap-2 border-b p-2">
                  <Input className="h-8 max-w-md text-xs" value={tableSearch}
                    onChange={e => setTableSearch(e.target.value)}
                    placeholder="Filter by PN, employee name, position code, job title, dims…" />
                  <div className="flex gap-1 rounded border bg-gray-50 p-0.5 text-[11px]">
                    {([
                      ['all', `All (${flagCounts.all})`],
                      ['unfunded_staff', `Unfunded staff (${flagCounts.unfunded_staff})`],
                      ['funded_vacancy', `Funded vacancy (${flagCounts.funded_vacancy})`],
                      ['unbudgeted_vacant', `Unbudgeted vacant (${flagCounts.unbudgeted_vacant})`],
                      ['funded_filled', `Funded & filled (${flagCounts.funded_filled})`],
                    ] as Array<[typeof flagFilter, string]>).map(([k, label]) => (
                      <button key={k}
                        className={cn('rounded px-2 py-0.5',
                          flagFilter === k ? 'bg-heritage-blue text-white' : 'text-gray-600 hover:bg-gray-100')}
                        onClick={() => setFlagFilter(k)}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <RolePicker options={roleOpts} selected={effectiveRoles} mode={roleMode} onChange={updateRoles} />
                  <Button variant="outline" size="sm" className="h-7 text-[11px]"
                    onClick={() => setCollapsed(new Set())}>Expand all</Button>
                  <Button variant="outline" size="sm" className="h-7 text-[11px]"
                    onClick={() => setCollapsed(new Set(groups.map(([t]) => t)))}>Collapse all</Button>
                  <span className="ml-auto text-[11px] text-gray-500">
                    {shownSeats} seats · {groups.length} roles
                  </span>
                </div>
                <div className="max-h-[58vh] overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 z-10 bg-slate-800 text-white">
                      <tr>
                        <th className="px-2 py-1.5 text-left font-medium">Seat / Incumbent</th>
                        <th className="px-2 py-1.5 text-left font-medium">Dims</th>
                        <th className="px-2 py-1.5 text-left font-medium">Lines</th>
                        <th className="px-2 py-1.5 text-right font-medium">Budget (yr)</th>
                        <th className="px-2 py-1.5 text-right font-medium" title="Jan → finalised month, CTC basis">YTD CTC</th>
                        <th className="px-2 py-1.5 text-right font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groups.map(([title, seats]) => (
                        <React.Fragment key={title}>
                          <tr className="cursor-pointer border-b bg-slate-50/80 hover:bg-slate-100"
                            onClick={() => toggleGroup(title)}>
                            <td colSpan={6} className="px-2 py-1 font-semibold text-slate-700">
                              <span className="mr-1 inline-block w-3 text-slate-400">{collapsed.has(title) && !tableSearch.trim() ? '▸' : '▾'}</span>
                              {title}
                              <span className="ml-2 font-normal text-gray-500">
                                {seats.filter(s => s.occupancy === 'filled').length} filled ·{' '}
                                {seats.filter(s => s.occupancy === 'vacant').length} vacant ·{' '}
                                {money(seats.reduce((t, s) => t + s.budgeted_annual, 0))} budget
                              </span>
                            </td>
                          </tr>
                          {(!collapsed.has(title) || !!tableSearch.trim()) && seats.map(s => (
                            <tr key={s.fo_position_id} className={cn('border-b last:border-0 hover:bg-muted/40', s.status === 'RETIRED' && 'opacity-60')}>
                              <td className="px-2 py-1">
                                <span className="font-mono text-[10px] text-gray-500">{s.fo_position_id}</span>{' '}
                                {s.incumbent
                                  ? <>{s.incumbent} <span className="text-gray-400">· {s.pn} · {s.employment}</span></>
                                  : (
                                    <span className="text-gray-400">
                                      (vacant){s.seat_type ? ` · ${s.seat_type}` : ''}
                                    </span>
                                  )}
                                {/* Grade band. Amber when the seat and its own JOB
                                    disagree — one was changed without the other. */}
                                {(s.grade || s.job_grade) && (
                                  <Badge variant="outline"
                                    className={cn('ml-1.5 font-mono text-[9px]',
                                      s.grade && s.job_grade && s.grade !== s.job_grade
                                        ? 'border-amber-300 bg-amber-50 text-amber-800'
                                        : 'border-slate-200 bg-slate-50 text-slate-600')}
                                    title={s.grade && s.job_grade && s.grade !== s.job_grade
                                      ? `Seat is graded ${s.grade} but its job ${s.fo_job_id || ''} is graded ${s.job_grade}`
                                      : `Grade ${s.grade || s.job_grade}${s.fo_job_id ? ` · job ${s.fo_job_id}` : ''}`}>
                                    {s.grade || s.job_grade}
                                    {s.grade && s.job_grade && s.grade !== s.job_grade && ` ≠ ${s.job_grade}`}
                                  </Badge>
                                )}
                                {s.flag && (
                                  <Badge variant="outline" className={cn('ml-1.5 text-[9px]', FLAG_META[s.flag].cls)}>
                                    {FLAG_META[s.flag].label}
                                  </Badge>
                                )}
                                {s.status === 'RETIRED' && (
                                  <Badge variant="outline" className="ml-1.5 border-gray-300 bg-gray-100 text-[9px] text-gray-500">
                                    Abolished
                                  </Badge>
                                )}
                                {s.funding_note && (
                                  <Badge variant="outline" className="ml-1 border-sky-200 bg-sky-50 text-[9px] text-sky-700"
                                    title={s.funding_note.note}>
                                    ⓘ {s.funding_note.ref ? `funded via ${s.funding_note.ref}` : 'funding note'}
                                  </Badge>
                                )}
                              </td>
                              <td className="whitespace-nowrap px-2 py-1 text-[10px] text-gray-500">
                                {[s.campus, s.department, s.faculty].filter(Boolean).join('·') || '—'}
                                {s.reports_to && (
                                  <span className="text-gray-400" title={`Reports to ${s.reports_to}${s.reports_to_label ? ` — ${s.reports_to_label}` : ''}`}>
                                    {' '}↳{s.reports_to}
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-1">
                                {s.lines.length
                                  ? s.lines.map(l => (
                                    // Actionable: a line that followed a PERSON onto the wrong
                                    // seat (the swapped-seats case) is moved FROM here — acting on
                                    // the SOURCE makes "whose funding you're taking" explicit,
                                    // which is why the Fund picker never offers filled seats' lines.
                                    <button key={l.item_id} type="button"
                                      className={cn('mr-1 rounded border px-1.5 py-0.5 text-[10px]',
                                        l.employment_type && (s.seat_type || s.employment)
                                          && l.employment_type !== (s.seat_type || s.employment)
                                          ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
                                          : 'border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100')}
                                      title={l.employment_type && (s.seat_type || s.employment)
                                        && l.employment_type !== (s.seat_type || s.employment)
                                        ? `RF1 line ${l.code} · ${money(l.annual)} · ${l.employment_type} budget on a ${s.seat_type || s.employment} seat — type mismatch; click to move it`
                                        : `RF1 line ${l.code} · ${money(l.annual)} — click to move it to another seat`}
                                      onClick={() => {
                                        const full = data?.lines.find(x => x.item_id === l.item_id);
                                        if (full) { setAction({ kind: 'move', line: full }); setReason(''); setTarget(''); }
                                      }}>
                                      {l.code}
                                      {l.employment_type && (s.seat_type || s.employment)
                                        && l.employment_type !== (s.seat_type || s.employment)
                                        && <span className="ml-1 font-medium">· {l.employment_type}</span>}
                                      {/* The band the BUDGET was written at. Shown
                                          only when it disagrees with the job — a
                                          matching grade is not news. */}
                                      {l.grade && s.job_grade && l.grade !== s.job_grade && (
                                        <span className="ml-1 font-medium text-violet-700"
                                          title={`Budgeted at grade ${l.grade}; the seat's job ${s.fo_job_id || ''} is graded ${s.job_grade}`}>
                                          · {l.grade}≠{s.job_grade}
                                        </span>
                                      )}
                                    </button>
                                  ))
                                  : <span className="text-gray-300">—</span>}
                              </td>
                              <td className={cn('px-2 py-1 text-right tabular-nums', s.budgeted_annual === 0 && 'text-gray-300')}>
                                {money(s.budgeted_annual)}
                              </td>
                              <td className={cn('px-2 py-1 text-right tabular-nums', s.actual_ytd_ctc === 0 && 'text-gray-300')}>
                                {money(s.actual_ytd_ctc)}
                              </td>
                              <td className="px-2 py-1 text-right whitespace-nowrap">
                                {s.status !== 'RETIRED' && s.occupancy === 'filled' && s.budgeted_annual === 0 && (
                                  <>
                                    <Button variant="outline" size="sm" className="h-6 px-1.5 text-[10px]"
                                      title="Move a funded line onto this person's seat"
                                      onClick={() => { setAction({ kind: 'fund', seat: s }); setReason(''); setTarget(''); }}>
                                      <ArrowRightLeft className="mr-0.5 h-3 w-3" /> Fund
                                    </Button>
                                    <Button variant="outline" size="sm" className="ml-1 h-6 px-1.5 text-[10px]"
                                      title="Record WHERE this person's funding actually comes from (e.g. a portion of another department's line) — shown on the row, ledgered"
                                      onClick={() => { setAction({ kind: 'note', seat: s }); setReason(s.funding_note?.note || ''); setNoteRef(s.funding_note?.ref || ''); }}>
                                      <StickyNote className="mr-0.5 h-3 w-3" /> Note
                                    </Button>
                                  </>
                                )}
                                {s.status !== 'RETIRED' && s.occupancy === 'vacant' && (
                                  <Button variant="outline" size="sm" className="ml-1 h-6 px-1.5 text-[10px]"
                                    title={s.budgeted_annual > 0
                                      ? 'Retire this seat — its budget lines will be released (audited)'
                                      : 'Retire this empty, unfunded seat'}
                                    onClick={() => { setAction({ kind: 'retire', seat: s }); setReason(''); }}>
                                    <Trash2 className="mr-0.5 h-3 w-3" /> Retire
                                  </Button>
                                )}
                                {/* Retirement used to be one-way, which dead-ended every
                                    "put the money back on the role they left" correction. */}
                                {s.status === 'RETIRED' && (
                                  <Button variant="outline" size="sm" className="ml-1 h-6 px-1.5 text-[10px]"
                                    title="Bring this abolished seat back as a live vacant seat, so a budget line can be moved onto it"
                                    onClick={() => { setAction({ kind: 'reinstate', seat: s }); setReason(''); }}>
                                    <Undo2 className="mr-0.5 h-3 w-3" /> Reinstate
                                  </Button>
                                )}
                                <Button variant="outline" size="sm" className="ml-1 h-6 px-1.5 text-[10px]"
                                  title={`Re-align this seat's structure: financial dimensions and/or reporting line${s.reports_to ? ` (currently reports to ${s.reports_to}${s.reports_to_label ? ` — ${s.reports_to_label}` : ''})` : ''}`}
                                  onClick={() => {
                                    setAction({ kind: 'reallocate', seat: s });
                                    setReason('');
                                    setReallocCampus(s.campus || '');
                                    setReallocDept(s.department || '');
                                    setReallocFac(s.faculty || 'NA');
                                    setReallocParent('');
                                    setReallocParentLabel('');
                                    setParentQuery('');
                                    setParentResults([]);
                                  }}>
                                  <MapPin className="mr-0.5 h-3 w-3" /> Structure
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Unallocated + released lines */}
            {(unallocated.length > 0 || released.length > 0 || adjustments.length > 0) && (
              <Card>
                <CardContent className="p-3 text-xs">
                  {unallocated.length > 0 && (
                    <>
                      <div className="mb-1 font-semibold text-amber-800">
                        Unallocated lines ({unallocated.length} · {money(unallocated.reduce((t, l) => t + l.annual, 0))})
                        <span className="ml-1 font-normal text-gray-500">— money in the plan not on any seat</span>
                      </div>
                      <div className="space-y-1">
                        {unallocated.map(l => (
                          <div key={l.item_id} className="flex items-center gap-2 rounded border bg-amber-50/40 px-2 py-1">
                            <span className="font-mono text-[10px]">{l.code}</span>
                            <span className="min-w-0 flex-1 truncate">{l.title}{l.name ? ` · ${l.name}` : ''}</span>
                            <span className="tabular-nums">{money(l.annual)}</span>
                            <Button variant="outline" size="sm" className="h-6 px-1.5 text-[10px]"
                              onClick={() => { setAction({ kind: 'move', line: l }); setReason(''); setTarget(''); }}>
                              <ArrowRightLeft className="mr-0.5 h-3 w-3" /> Assign
                            </Button>
                            <Button variant="outline" size="sm" className="h-6 px-1.5 text-[10px]"
                              onClick={() => { setAction({ kind: 'release', line: l }); setReason(''); }}>
                              Release
                            </Button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {adjustments.length > 0 && (
                    <div className="mt-2 text-gray-500"
                      title="RF1 envelope adjustments (removals/savings/specials) — they reduce the envelope but never fund seats; excluded from the unallocated total and the bridge's Original.">
                      Envelope adjustments: {adjustments.map(l => `${l.code} · ${l.title} (${money(l.annual)})`).join(' · ')}
                    </div>
                  )}
                  {released.length > 0 && (
                    <div className="mt-2 text-gray-500">
                      Released: {released.map(l => `${l.code} (${money(l.annual)})`).join(' · ')}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Change requests — what department heads have ASKED for. Separate
              from the ledger on purpose: the ledger is what happened, this is
              what somebody wants. Accepting one records a decision and nothing
              else; the change itself is still made by hand below. */}
          {reqOpen && (
            <Card className="self-start">
              <CardContent className="p-3">
                <div className="mb-1.5 flex items-center gap-1.5">
                  <span className="text-sm font-medium text-gray-700">Change requests</span>
                  <span className="text-[11px] text-gray-500">
                    {openRequests.length} open{requests.length !== openRequests.length
                      ? ` · ${requests.length - openRequests.length} resolved` : ''}
                  </span>
                </div>
                {!requests.length ? (
                  <p className="py-2 text-xs text-gray-500">
                    {readOnly
                      ? 'You have not raised any requests for this slice.'
                      : 'No requests for this slice.'}
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {requests.map(r => (
                      <div key={r.id} className={cn(
                        'rounded border p-2 text-xs',
                        r.status === 'open' ? 'border-amber-300 bg-amber-50/40' : 'border-gray-200 bg-gray-50',
                      )}>
                        <div className="flex items-start gap-2">
                          <span className="rounded bg-white px-1.5 py-0.5 text-[10px] font-medium uppercase text-gray-600 ring-1 ring-gray-200">
                            {r.request_type.replace(/_/g, ' ')}
                          </span>
                          {r.fo_position_id && (
                            <span className="font-mono text-[11px] text-gray-700">{r.fo_position_id}</span>
                          )}
                          {r.seat_title && <span className="truncate text-gray-600">{r.seat_title}</span>}
                          <span className={cn(
                            'ml-auto text-[10px] font-medium',
                            r.status === 'open' ? 'text-amber-700'
                              : r.status === 'accepted' ? 'text-emerald-700' : 'text-gray-500',
                          )}>{r.status}</span>
                        </div>
                        <p className="mt-1 text-gray-700">{r.detail}</p>
                        <p className="mt-0.5 text-[10px] text-gray-500">
                          {r.requested_by}{r.requested_at ? ` · ${r.requested_at.slice(0, 10)}` : ''}
                          {r.resolution_note ? ` · ${r.resolution_note}` : ''}
                        </p>

                        {/* Only a budget role holder can disposition, and doing
                            so never performs the change. */}
                        {!readOnly && r.status === 'open' && (
                          declineFor === r.id ? (
                            <div className="mt-1.5 flex items-center gap-1.5">
                              <input autoFocus className="h-7 flex-1 rounded border px-2 text-[11px]"
                                placeholder="Why are you declining? The requester sees this."
                                value={declineNote} onChange={e => setDeclineNote(e.target.value)} />
                              <Button size="sm" variant="outline" className="h-7 text-[11px]"
                                disabled={reqBusy === r.id || declineNote.trim().length < 5}
                                onClick={() => resolveRequest(r.id, 'declined', declineNote.trim())}>
                                Decline
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 text-[11px]"
                                onClick={() => { setDeclineFor(null); setDeclineNote(''); }}>
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <div className="mt-1.5 flex items-center gap-1.5">
                              <Button size="sm" variant="outline" className="h-7 text-[11px]"
                                disabled={reqBusy === r.id}
                                onClick={() => resolveRequest(r.id, 'accepted')}
                                title="Records the decision and points you at the seat. It does NOT make the change.">
                                {reqBusy === r.id
                                  ? <Loader2 className="h-3 w-3 animate-spin" />
                                  : 'Accept'}
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 text-[11px]"
                                disabled={reqBusy === r.id}
                                onClick={() => setDeclineFor(r.id)}>
                                Decline
                              </Button>
                              <span className="text-[10px] text-gray-500">
                                Accepting records the decision — you still make the change yourself.
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Ledger — global with filters + click-to-navigate */}
          {showLedger && (
            <Card className="self-start">
              <CardContent className="p-3">
                <div className="mb-1.5 flex items-center gap-1.5">
                  <span className="text-sm font-medium text-gray-700">Change ledger</span>
                  <div className="ml-auto flex gap-0.5 rounded border bg-gray-50 p-0.5 text-[10px]">
                    <button className={cn('rounded px-1.5 py-0.5', ledgerScope === 'slice' ? 'bg-heritage-blue text-white' : 'text-gray-600')}
                      onClick={() => setLedgerScope('slice')}>This slice</button>
                    <button className={cn('rounded px-1.5 py-0.5', ledgerScope === 'all' ? 'bg-heritage-blue text-white' : 'text-gray-600')}
                      onClick={() => setLedgerScope('all')}>All slices</button>
                  </div>
                </div>
                <div className="mb-1.5 flex gap-1.5">
                  <Input className="h-7 flex-1 text-[11px]" placeholder="Filter reason, code, seat, actor…"
                    value={ledgerSearch} onChange={e => setLedgerSearch(e.target.value)} />
                  <select className="h-7 rounded border border-gray-200 px-1 text-[11px] text-gray-700"
                    value={ledgerAction} onChange={e => setLedgerAction(e.target.value)}>
                    <option value="">All actions</option>
                    {Object.entries(ledger?.by_action ?? {}).map(([a, c]) => (
                      <option key={a} value={a}>{a.replace(/_/g, ' ')} ({c.count})</option>
                    ))}
                  </select>
                </div>
                {!ledger?.entries.length ? (
                  <div className="py-6 text-center text-xs text-gray-400">
                    No adjustments {ledgerScope === 'slice' ? 'for this slice' : 'yet'}.
                  </div>
                ) : (
                  <div className="max-h-[48vh] space-y-1.5 overflow-auto text-[11px]">
                    {ledger.entries.map(e => (
                      <div key={e.id}
                        className="cursor-pointer rounded border bg-gray-50/60 p-1.5 hover:border-heritage-blue/40 hover:bg-sky-50/50"
                        title="Open this change's slice with the affected seat filtered"
                        onClick={() => navigateToEntry(e)}>
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold">{e.action.replace(/_/g, ' ')}</span>
                          {e.rf1_code && <span className="font-mono text-[10px] text-gray-500">{e.rf1_code}</span>}
                          <span className="text-[10px] text-gray-400">{[e.campus_code, e.department].filter(Boolean).join('·')}</span>
                          {typeof e.annual_amount === 'number' && e.annual_amount !== 0 && (
                            <span className="ml-auto tabular-nums">{money(e.annual_amount)}</span>
                          )}
                        </div>
                        {entryTransition(e) && (
                          <div className="text-gray-500">{entryTransition(e)}</div>
                        )}
                        <div className="text-gray-600">“{e.reason}”</div>
                        <div className="flex items-center text-[10px] text-gray-400">
                          <span>{e.at?.slice(0, 16)} · {e.actor}</span>
                          {e.action === 'move_line' && e.item_id && e.from_fo_position_id && (
                            <button type="button"
                              className="ml-auto rounded border border-gray-300 px-1.5 py-0.5 text-[10px] text-gray-600 hover:bg-gray-100"
                              title={`Move ${e.rf1_code} back to ${e.from_fo_position_id} (mirror move, ledgered)`}
                              onClick={ev => { ev.stopPropagation(); undoMove(e); }}
                              disabled={undoBusy === e.id}>
                              {undoBusy === e.id ? '…' : '↩ Undo'}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    {ledger.truncated && (
                      <div className="py-1 text-center text-[10px] text-gray-400">Showing the latest {ledger.entries.length} — narrow the filters for more.</div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Action dialog */}
      <Dialog open={!!action} onOpenChange={o => { if (!o) closeDialog(); }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-sm">
              {action?.kind === 'fund' && <>Fund {action.seat.incumbent} · {action.seat.title} ({action.seat.fo_position_id})</>}
              {action?.kind === 'move' && <>Assign line {action.line.code} · {money(action.line.annual)}</>}
              {action?.kind === 'release' && <>Release line {action.line.code} · {money(action.line.annual)}</>}
              {action?.kind === 'retire' && <>Retire seat {action.seat.fo_position_id} · {action.seat.title}</>}
              {action?.kind === 'reinstate' && <>Reinstate seat {action.seat.fo_position_id} · {action.seat.title}</>}
              {action?.kind === 'reallocate' && <>Structure · {action.seat.fo_position_id} · {action.seat.title}</>}
              {action?.kind === 'note' && <>Funding note · {action.seat.incumbent || action.seat.fo_position_id} · {action.seat.title}</>}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-2 text-xs">
            {action?.kind === 'fund' && (
              <>
                <div className="text-gray-600">
                  Pick the budget line to move onto this seat (unallocated lines first, then lines currently parked on funded vacancies)
                  {(action.seat.seat_type || action.seat.employment) && (
                    <> — <b>{action.seat.seat_type || action.seat.employment}</b> seat, so only {action.seat.seat_type || action.seat.employment} budget is offered</>
                  )}:
                </div>
                <SearchPick placeholder="Search by PN, name, code, job title, dims…" value={target} onChange={setTarget}
                  options={fundCandidates.map(l => ({
                    value: String(l.item_id),
                    code: l.code,
                    title: l.title || '—',
                    person: l.name || undefined,
                    pn: l.employee_code && l.employee_code !== l.code ? l.employee_code : undefined,
                    dims: [l.campus, l.department, l.employment_type, l.grade].filter(Boolean).join('·'),
                    amount: money(l.annual),
                    note: (action.seat.seat_type || action.seat.employment)
                      && l.employment_type
                      && l.employment_type !== (action.seat.seat_type || action.seat.employment)
                      ? `⚠ ${l.employment_type} line`
                      : l.state === 'linked' ? `on vacant ${l.linked_fo}` : 'unallocated',
                  }))} />
                {fundBlockedByType > 0 && !allowCrossType && (
                  <div className="text-[11px] text-amber-700">
                    {fundBlockedByType} same-slice line(s) hidden — different position type
                    (budget is held per type; moving it would mis-fund the seat).
                  </div>
                )}
                <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-gray-600">
                  <input type="checkbox" checked={allowCrossType}
                    onChange={e => setAllowCrossType(e.target.checked)} />
                  Allow a different position type (deliberate override — your reason justifies it and the
                  ledger records the cross-type move)
                </label>
              </>
            )}
            {action?.kind === 'move' && (
              <>
                {(() => {
                  // Moving a line OFF a filled seat defunds its incumbent — say so
                  // plainly (the swapped-seats case: money followed the person, must
                  // follow the role; the incumbent then needs their own line).
                  const src = action.line.linked_fo
                    ? data?.seats.find(x => x.fo_position_id === action.line.linked_fo) : null;
                  return src && src.occupancy === 'filled' ? (
                    <div className="flex items-start gap-1.5 rounded border border-amber-200 bg-amber-50 p-2 text-amber-800">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      This line currently funds <b>{src.incumbent}</b> ({src.fo_position_id}) — moving it
                      DEFUNDS them: they will show as Unfunded staff until another line is assigned
                      (e.g. the vacancy line budgeted for their new role).
                    </div>
                  ) : null;
                })()}
                <div className="text-gray-600">
                  Destination — only seats that NEED funding are offered (unfunded staff first,
                  same-role matches on top, then unbudgeted vacant seats). Funded seats are
                  excluded to prevent double-funding
                  {action.line.employment_type && (
                    <>, and this is <b>{action.line.employment_type}</b> budget so only {action.line.employment_type} seats qualify</>
                  )}.
                </div>
                <SearchPick placeholder="Search by PN, incumbent, seat, job title, dims…" value={target} onChange={setTarget}
                  options={moveCandidates.map(s => ({
                    value: s.fo_position_id,
                    code: s.fo_position_id,
                    title: s.title,
                    person: s.incumbent || '(vacant)',
                    pn: s.pn || undefined,
                    dims: [s.campus, s.department, s.seat_type || s.employment].filter(Boolean).join('·'),
                    amount: s.budgeted_annual > 0 ? money(s.budgeted_annual) : '—',
                    note: action.line.employment_type
                      && (s.seat_type || s.employment)
                      && (s.seat_type || s.employment) !== action.line.employment_type
                      ? `⚠ ${s.seat_type || s.employment} seat`
                      : s.budgeted_annual > 0 ? 'funded' : 'unfunded',
                  }))} />
                {moveBlockedByType > 0 && !allowCrossType && (
                  <div className="text-[11px] text-amber-700">
                    {moveBlockedByType} unfunded seat(s) hidden — different position type.
                  </div>
                )}
                <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-gray-600">
                  <input type="checkbox" checked={allowCrossType}
                    onChange={e => setAllowCrossType(e.target.checked)} />
                  Allow a different position type (deliberate override — justified by your reason and
                  recorded on the ledger)
                </label>

                {/* Swap: the destination seat is already funded, so the two
                    lines trade places in one atomic, single-reason act. */}
                <label className="mt-1.5 flex cursor-pointer items-center gap-1.5 text-gray-600">
                  <input type="checkbox" checked={allowStacking}
                    onChange={e => setAllowStacking(e.target.checked)} />
                  <b>Stack</b> onto a seat that is already funded (a deliberate multi-line
                  seat — the lane becomes the SUM, and it is recorded on the ledger)
                </label>
                <div className="mt-2 border-t pt-2">
                  <label className="flex cursor-pointer items-center gap-1.5 text-gray-600">
                    <input type="checkbox" checked={swapMode}
                      onChange={e => { setSwapMode(e.target.checked); setSwapTarget(null); setTarget(''); }} />
                    <b>Swap</b> with the line on an already-funded seat (both seats keep funding — one act, one reason)
                  </label>
                  {swapMode && (
                    <div className="mt-1 max-h-44 overflow-y-auto rounded border">
                      {swapCandidates.length ? swapCandidates.map(({ seat, line }) => {
                        const clash = !!(action.line.employment_type && (seat.seat_type || seat.employment)
                          && (seat.seat_type || seat.employment) !== action.line.employment_type);
                        return (
                          <button key={seat.fo_position_id} type="button"
                            disabled={clash && !allowCrossType}
                            className={cn('flex w-full items-center gap-2 border-b px-2 py-1.5 text-left last:border-0',
                              clash && !allowCrossType ? 'cursor-not-allowed opacity-50' : 'hover:bg-muted/40',
                              swapTarget === line.item_id && 'bg-emerald-50')}
                            onClick={() => setSwapTarget(line.item_id)}>
                            <span className="font-mono text-[10px] text-gray-600">{seat.fo_position_id}</span>
                            <span className="min-w-0 flex-1 truncate">
                              {seat.title} · {seat.incumbent || '(vacant)'}
                            </span>
                            <span className="font-mono text-[10px] text-sky-700">{line.code}</span>
                            <span className="tabular-nums">{money(line.annual)}</span>
                            {swapTarget === line.item_id && <span className="text-[10px] text-emerald-700">✓</span>}
                          </button>
                        );
                      }) : (
                        <div className="px-2 py-2 text-[11px] text-gray-400">No funded single-line seats in this slice to swap with.</div>
                      )}
                    </div>
                  )}
                  {swapMode && swapTarget && (
                    <div className="mt-1 text-[11px] text-gray-600">
                      {action.line.code} moves to that seat, and its line moves to
                      {' '}<span className="font-mono">{action.line.linked_fo || '—'}</span>. Both stay funded.
                    </div>
                  )}
                </div>

                {/* Cross-slice destination — the line followed its person out of
                    this slice, so its rightful target may live in another
                    department (three Receptionist replacements, 30 Jul). */}
                <div className="mt-2 border-t pt-2">
                  <div className="mb-1 text-gray-600">
                    Destination not in the list? Search <b>ALL seats</b> — other departments, or seats
                    with <b>missing dims</b> (those belong to no slice at all) — by PN, name, seat code or title:
                  </div>
                  <div className="relative">
                    <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-gray-400" />
                    <Input className="h-8 pl-7 text-xs" value={xsQ}
                      placeholder="e.g. 43xxxxx or Receptionist…"
                      onChange={e => searchSeatsGlobal(e.target.value)} />
                  </div>
                  {xsBusy && <div className="py-1 text-[10px] text-gray-400">Searching…</div>}
                  {!xsBusy && xsResults.length > 0 && (
                    <div className="mt-1 max-h-44 overflow-y-auto rounded border">
                      {xsResults.map(s => {
                        const typeClash = !!(action.line.employment_type && s.seat_type
                          && s.seat_type !== action.line.employment_type);
                        const blocked = s.budgeted_annual > 0 || (typeClash && !allowCrossType);
                        return (
                          <button key={s.fo_position_id} type="button" disabled={blocked}
                            className={cn('flex w-full items-center gap-2 border-b px-2 py-1.5 text-left last:border-0',
                              blocked ? 'cursor-not-allowed opacity-50' : 'hover:bg-muted/40',
                              target === s.fo_position_id && 'bg-emerald-50')}
                            title={s.budgeted_annual > 0 ? 'Already funded — moving here would double-fund'
                              : typeClash ? `Type mismatch: ${action.line.employment_type} line vs ${s.seat_type} seat`
                              : undefined}
                            onClick={() => setTarget(s.fo_position_id)}>
                            <span className="font-mono text-[10px] text-gray-600">{s.fo_position_id}</span>
                            <span className="min-w-0 flex-1 truncate">
                              {s.title} · {s.incumbent || '(vacant)'}{s.pn ? ` (${s.pn})` : ''}
                            </span>
                            <span className="text-[10px] text-gray-500">
                              {[s.campus, s.department, s.seat_type].filter(Boolean).join('·')}
                            </span>
                            <span className="text-[10px]">
                              {s.budgeted_annual > 0 ? `funded ${money(s.budgeted_annual)}` : typeClash ? 'wrong type' : 'unfunded'}
                            </span>
                            {target === s.fo_position_id && <span className="text-[10px] text-emerald-700">✓ selected</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
            {action?.kind === 'note' && (
              <div className="space-y-1.5">
                <div className="text-gray-600">
                  Explain WHERE this seat's funding actually comes from (shown on the row — the seat
                  keeps its true budget state, but the red chip stops being a mystery). Use the reason
                  field below for the note itself.
                </div>
                <label>
                  <span className="text-[10px] text-gray-600">Funding source line (optional — the code of the line a portion comes from)</span>
                  <Input className="h-8 text-xs" value={noteRef} placeholder="e.g. 43xxxxx"
                    onChange={e => setNoteRef(e.target.value)} />
                </label>
              </div>
            )}
            {action?.kind === 'reinstate' && (
              <div className="space-y-1.5">
                <div className="rounded border border-sky-200 bg-sky-50 p-2 text-sky-800">
                  This re-opens the position in F&amp;O (its duration runs to 2154 again) and brings it
                  back here as a <b>vacant</b> seat, so a budget line can be moved onto it. Nothing is
                  funded by this act.
                </div>
                <div className="text-gray-600">
                  The reporting line is <b>not</b> restored — retiring trimmed that edge in F&amp;O. A seat
                  coming back usually also needs its title carried through the 1&nbsp;Aug renames and its
                  job re-keyed: do both, plus the parent, in <b>Structure</b> once it is live.
                </div>
              </div>
            )}
            {action?.kind === 'retire' && action.seat.actual_ytd_ctc > 0 && (
              <div className="rounded border border-sky-200 bg-sky-50 p-2 text-sky-800">
                This seat carries {money(action.seat.actual_ytd_ctc)} of YTD actuals (a leaver's part-year
                spend). Retiring does NOT delete them: the seat and its actuals stay on the position
                record, in the reconciliation export (Occupancy = Abolished), and remain visible here
                via "Show abolished" — only its vacancy disappears from the establishment.
              </div>
            )}
            {action?.kind === 'retire' && action.seat.budgeted_annual > 0 && (
              <div className="flex items-start gap-1.5 rounded border border-amber-200 bg-amber-50 p-2 text-amber-800">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                This seat holds {money(action.seat.budgeted_annual)} of budget
                ({action.seat.lines.map(l => l.code).join(', ')}). Retiring will RELEASE those lines
                from the baseline (each logged with your reason). To keep the money, Cancel and move
                the line to another seat first.
              </div>
            )}
            {action?.kind === 'reallocate' && (
              <div className="space-y-1.5">
                <div className="text-gray-600">
                  Current: <b>{action.seat.campus || '—'} / {action.seat.department || '—'} / {action.seat.faculty || 'NA'}</b> — pick the target dimensions:
                </div>
                <div className="flex gap-2">
                  <Select value={reallocCampus} onValueChange={setReallocCampus}>
                    <SelectTrigger className="h-8 flex-1 text-xs"><SelectValue placeholder="Campus" /></SelectTrigger>
                    <SelectContent>
                      {(options?.campus ?? []).filter(c => c !== '(Unassigned)').map(c => (
                        <SelectItem key={c} value={c}>{c}{c === action.seat.campus ? ' (current)' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={reallocDept} onValueChange={setReallocDept}>
                    <SelectTrigger className="h-8 flex-1 text-xs"><SelectValue placeholder="Department" /></SelectTrigger>
                    <SelectContent>
                      {(options?.department ?? []).filter(d => d !== '(Unassigned)').map(d => (
                        <SelectItem key={d} value={d}>{d}{d === action.seat.department ? ' (current)' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={reallocFac} onValueChange={setReallocFac}>
                    <SelectTrigger className="h-8 flex-1 text-xs"><SelectValue placeholder="Faculty" /></SelectTrigger>
                    <SelectContent>
                      {['NA', ...(options?.faculty ?? []).filter(f => f !== '(Unassigned)' && f !== 'NA')].map(f => (
                        <SelectItem key={f} value={f}>{f}{f === (action.seat.faculty || 'NA') ? ' (current)' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Reporting line — same F&O write path as /organization's
                    "Set reporting line" (vacant = Line edge; filled = saga). */}
                <div className="mt-2 border-t pt-2">
                  <div className="mb-1 flex items-center gap-1 text-gray-600">
                    <GitBranch className="h-3 w-3" />
                    Reporting line — currently{' '}
                    <b>
                      {action.seat.reports_to
                        ? `${action.seat.reports_to}${action.seat.reports_to_label ? ` (${action.seat.reports_to_label})` : ''}`
                        : 'not set'}
                    </b>
                    <span className="text-gray-400">(leave blank to keep)</span>
                  </div>
                  {reallocParent ? (
                    <div className="flex items-center gap-2 rounded border border-emerald-200 bg-emerald-50 px-2 py-1">
                      <span className="font-mono text-[10px]">{reallocParent}</span>
                      <span className="min-w-0 flex-1 truncate">{reallocParentLabel}</span>
                      <button type="button" className="text-[10px] text-gray-500 hover:underline"
                        onClick={() => { setReallocParent(''); setReallocParentLabel(''); }}>
                        clear
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-gray-400" />
                        <Input className="h-8 pl-7 text-xs" value={parentQuery}
                          placeholder="Search the new parent seat by title, code or incumbent…"
                          onChange={e => {
                            setParentQuery(e.target.value);
                            searchParents(e.target.value, action.seat.fo_position_id);
                          }} />
                      </div>
                      {parentSearching && <div className="py-1 text-[10px] text-gray-400">Searching…</div>}
                      {!parentSearching && parentResults.length > 0 && (
                        <div className="mt-1 max-h-40 overflow-y-auto rounded border">
                          {parentResults.map(p => (
                            <button key={p.code} type="button"
                              className="flex w-full items-center gap-2 border-b px-2 py-1.5 text-left last:border-0 hover:bg-muted/40"
                              onClick={() => { setReallocParent(p.code); setReallocParentLabel(`${p.who} · ${p.title}`); }}>
                              <span className="font-mono text-[10px] text-gray-600">{p.code}</span>
                              <span className="min-w-0 flex-1 truncate">{p.title}</span>
                              <span className="text-[10px] text-gray-500">{p.who}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {action.seat.occupancy === 'filled' && reallocParent && (
                    <div className="mt-1 text-[10px] text-amber-700">
                      Filled seat — the change runs the payroll-sync saga (Sage "Reports To" follows), which can take up to a minute.
                    </div>
                  )}
                </div>

                {/* Job / role — re-point the seat at a different Role Master job
                    (Jobs-tab reassign, seat-grain: junior-band HRBP vacancies
                    under the senior job belong on RM-0131). */}
                <div className="mt-2 border-t pt-2">
                  <div className="mb-1 text-gray-600">
                    Job / role — currently <b>{action.seat.fo_job_id || '—'}</b> ({action.seat.title})
                    <span className="text-gray-400"> (leave blank to keep)</span>
                  </div>
                  {reallocJob ? (
                    <div className="flex items-center gap-2 rounded border border-emerald-200 bg-emerald-50 px-2 py-1">
                      <span className="font-mono text-[10px]">{reallocJob}</span>
                      <span className="min-w-0 flex-1 truncate">{reallocJobLabel}</span>
                      <button type="button" className="text-[10px] text-gray-500 hover:underline"
                        onClick={() => { setReallocJob(''); setReallocJobLabel(''); }}>clear</button>
                    </div>
                  ) : (
                    <>
                      <Input className="h-8 text-xs" value={jobQ}
                        placeholder="Search the target job by title or RM code…"
                        onChange={e => searchJobs(e.target.value)} />
                      {jobBusy && <div className="py-1 text-[10px] text-gray-400">Searching…</div>}
                      {!jobBusy && jobQ.trim().length > 1 && (
                        <div className="mt-1 max-h-32 overflow-y-auto rounded border bg-white">
                          {jobResults.filter(j => j.job_id !== action.seat.fo_job_id).map(j => (
                            <button key={j.job_id} type="button"
                              className="flex w-full items-center gap-2 border-b px-2 py-1 text-left last:border-0 hover:bg-muted/40"
                              onClick={() => { setReallocJob(j.job_id); setReallocJobLabel(j.title); }}>
                              <span className="font-mono text-[10px] text-gray-600">{j.job_id}</span>
                              <span className="min-w-0 flex-1 truncate">{j.title}</span>
                              <span className="text-[10px] text-gray-400">{j.seats} seat(s)</span>
                            </button>
                          ))}
                          {!jobResults.length && (
                            <div className="px-2 py-1.5 text-[10px] text-gray-400">No job matches.</div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {reallocJob && action.seat.occupancy === 'filled' && (
                    <div className="mt-1 text-[10px] text-amber-700">
                      Filled seat — the incumbent's title follows the new job in F&O.
                    </div>
                  )}
                </div>
              </div>
            )}
            <div>
              <div className="mb-1 text-gray-600">Reason <span className="text-red-500">*</span> — goes on the audit ledger:</div>
              <Input className="h-8 text-xs" value={reason} onChange={e => setReason(e.target.value)}
                placeholder="e.g. Hired into funded vacancy per HOD session 22 Jul" />
            </div>
          </div>

          {/* Split-line panel: N parts, exact-total guard mirrors the API. */}
          {action?.kind === 'move' && splitOpen && (() => {
            const total = action.line.annual;
            const sum = splitParts.reduce((t, x) => t + (parseFloat(x.annual) || 0), 0);
            const rem = Math.round((total - sum) * 100) / 100;
            return (
              <div className="space-y-2 rounded border border-violet-200 bg-violet-50 p-2 text-xs">
                <div className="font-semibold text-violet-800">
                  Split {action.line.code} ({money(total)}{action.line.grade ? ` · ${action.line.grade}` : ''}) into {splitParts.length} parts
                </div>
                {splitParts.map((part, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-14 font-mono text-[10px] text-gray-600">{action.line.code}/{i + 1}</span>
                    <Input className="h-7 w-28 text-xs" value={part.annual} placeholder="Annual"
                      onChange={e => setSplitParts(ps => ps.map((x, j) => j === i ? { ...x, annual: e.target.value } : x))} />
                    <Input className="h-7 flex-1 text-xs" value={part.title} placeholder="Part title (optional — e.g. Recruitment Consultant C3)"
                      onChange={e => setSplitParts(ps => ps.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} />
                    {splitParts.length > 2 && (
                      <button type="button" className="text-[10px] text-gray-500 hover:underline"
                        onClick={() => setSplitParts(ps => ps.filter((_, j) => j !== i))}>remove</button>
                    )}
                  </div>
                ))}
                <div className="flex items-center gap-3">
                  <button type="button" className="text-[11px] text-violet-700 hover:underline"
                    onClick={() => setSplitParts(ps => [...ps, { annual: '0', title: action?.kind === 'move' ? (action.line.title || '') : '' }])}>
                    + add part
                  </button>
                  <span className={cn('text-[11px] tabular-nums', Math.abs(rem) > 1 ? 'text-red-600 font-semibold' : 'text-emerald-700')}>
                    {Math.abs(rem) > 1 ? `R${rem.toLocaleString()} unassigned — parts must sum exactly to the line` : 'Parts sum matches ✓'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" className="h-7 text-xs"
                    disabled={busy || reason.trim().length < 5 || Math.abs(rem) > 1}
                    onClick={async () => {
                      if (action?.kind !== 'move') return;
                      setBusy(true);
                      try {
                        const r = await budgetService.workbenchSplitLine(action.line.item_id,
                          splitParts.map(x => ({ annual: parseFloat(x.annual) || 0, title: x.title || undefined })), reason);
                        setFlash({ ok: true, text: `${action.line.code} split into ${r.children.join(', ')} — fund each via Assign` });
                        closeDialog(); load(); loadLedger();
                      } catch (e: any) {
                        const detail = e?.response?.data?.detail;
                        setFlash({ ok: false, text: typeof detail === 'string' ? detail : (e?.message || 'Split failed') });
                      } finally { setBusy(false); }
                    }}>
                    {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                    Split into {splitParts.length}
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-xs"
                    onClick={() => setSplitOpen(false)} disabled={busy}>Cancel</Button>
                </div>
              </div>
            );
          })()}

          {/* Review panel for "Request new seat" — shows EXACTLY what will be
              created, all of it editable (defaults come from the line). */}
          {action?.kind === 'move' && seatReqOpen && (
            <div className="space-y-2 rounded border border-sky-200 bg-sky-50 p-2 text-xs">
              <div className="font-semibold text-sky-800">
                New seat to request — funded by {action.line.code} ({money(action.line.annual)})
              </div>
              <div className="flex flex-wrap gap-2">
                <label className="flex-1 basis-full">
                  <span className="text-[10px] text-gray-600">Seat title</span>
                  <Input className="h-7 text-xs" value={seatReqTitle}
                    onChange={e => setSeatReqTitle(e.target.value)} />
                </label>
                <label className="flex-1">
                  <span className="text-[10px] text-gray-600">Campus</span>
                  <Input className="h-7 text-xs" value={seatReqCampus}
                    onChange={e => setSeatReqCampus(e.target.value.toUpperCase())} />
                </label>
                <label className="flex-1">
                  <span className="text-[10px] text-gray-600">Department</span>
                  <Input className="h-7 text-xs" value={seatReqDept}
                    onChange={e => setSeatReqDept(e.target.value)} />
                </label>
                <label className="flex-1">
                  <span className="text-[10px] text-gray-600">Faculty</span>
                  <Input className="h-7 text-xs" value={seatReqFaculty}
                    onChange={e => setSeatReqFaculty(e.target.value)} />
                </label>
              </div>
              <div>
                <span className="text-[10px] text-gray-600">
                  F&amp;O job (the role the seat is created under
                  {action.line.employment_type ? ` · type ${action.line.employment_type} from the line` : ''})
                </span>
                {seatReqJobChoice ? (
                  <div className="mt-0.5 flex items-center gap-2 rounded border border-emerald-200 bg-white px-2 py-1">
                    <span className="font-mono text-[10px]">{seatReqJobChoice}</span>
                    <span className="min-w-0 flex-1 truncate">{seatReqJobLabel}</span>
                    <button type="button" className="text-[10px] text-gray-500 hover:underline"
                      onClick={() => { setSeatReqJobChoice(''); setSeatReqJobLabel(''); }}>change</button>
                  </div>
                ) : (
                  <>
                    <Input className="mt-0.5 h-7 text-xs" value={jobQ}
                      placeholder="Search jobs by title or RM code…"
                      onChange={e => searchJobs(e.target.value)} />
                    {jobBusy && <div className="text-[10px] text-gray-400">Searching…</div>}
                    {!jobBusy && (
                      <div className="mt-1 max-h-28 overflow-y-auto rounded border bg-white">
                        {jobResults.length ? jobResults.map(j => (
                          <button key={j.job_id} type="button"
                            className="flex w-full items-center gap-2 border-b px-2 py-1 text-left last:border-0 hover:bg-muted/40"
                            onClick={() => { setSeatReqJobChoice(j.job_id); setSeatReqJobLabel(j.title); }}>
                            <span className="font-mono text-[10px] text-gray-600">{j.job_id}</span>
                            <span className="min-w-0 flex-1 truncate">{j.title}</span>
                            <span className={`text-[10px] ${j.seats ? 'text-gray-400' : 'text-emerald-700'}`}>
                              {j.seats ? `${j.seats} seat(s)` : 'new role · no seats yet'}
                            </span>
                          </button>
                        )) : (
                          <div className="px-2 py-1.5 text-[10px] text-amber-700">
                            No job matches — this role may not exist in F&amp;O yet; create it in Role Master first,
                            or pick the closest existing job.
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" className="h-7 text-xs" onClick={requestSeat}
                  disabled={busy || reason.trim().length < 5 || !seatReqJobChoice || !seatReqTitle.trim()}>
                  {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Inbox className="mr-1 h-3 w-3" />}
                  Raise request
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs"
                  onClick={() => setSeatReqOpen(false)} disabled={busy}>Cancel</Button>
                {!seatReqJobChoice && <span className="text-[10px] text-amber-700">Pick a job to continue</span>}
              </div>
            </div>
          )}
          <DialogFooter>
            {/* The line has no valid destination SEAT because none exists —
                raise a NEW_POSITION request from the line itself: pre-filled,
                funding pre-attached, approved on the Requests tab, seat born
                funded at provisioning. */}
            {action?.kind === 'move' && !seatReqOpen && (
              <Button variant="outline" size="sm"
                className="border-sky-200 text-sky-700 hover:bg-sky-50"
                disabled={busy}
                title="No suitable seat exists? Review and raise a new-position request pre-filled from this line — it arrives on the Requests tab with this line attached as funding"
                onClick={() => { if (action?.kind === 'move') openSeatRequest(action.line); }}>
                <Inbox className="mr-1 h-3.5 w-3.5" /> Request new seat…
              </Button>
            )}
            {/* A LINKED line opened for moving may turn out to need RELEASING
                instead — e.g. a double-counted A-suffix line on a multi-line
                seat has no valid destination; the money should leave the
                baseline (audited), not land somewhere else. */}
            {action?.kind === 'move' && !splitOpen && (
              <Button variant="outline" size="sm"
                className="border-violet-200 text-violet-700 hover:bg-violet-50"
                disabled={busy}
                title="Split this line into two or more fundable parts (parts must sum exactly to the line — no double-counting possible)"
                onClick={() => {
                  if (action?.kind !== 'move') return;
                  const half = Math.round(action.line.annual / 2 * 100) / 100;
                  setSplitOpen(true);
                  setSplitParts([
                    { annual: String(half), title: action.line.title || '' },
                    { annual: String(Math.round((action.line.annual - half) * 100) / 100), title: action.line.title || '' },
                  ]);
                }}>
                Split line…
              </Button>
            )}
            {action?.kind === 'move' && action.line.state === 'linked' && (
              <Button variant="outline" size="sm"
                className="mr-auto border-red-200 text-red-600 hover:bg-red-50"
                disabled={busy || reason.trim().length < 5}
                title="Remove this line from the baseline entirely (e.g. a double-count) — the money leaves the funded envelope, recorded on the ledger with your reason"
                onClick={async () => {
                  if (action?.kind !== 'move') return;
                  setBusy(true);
                  try {
                    await budgetService.workbenchReleaseLine(action.line.item_id, reason);
                    setFlash({ ok: true, text: `${action.line.code} released (${money(action.line.annual)}) — left the baseline` });
                    closeDialog(); load(); loadLedger();
                  } catch (e: any) {
                    const detail = e?.response?.data?.detail;
                    setFlash({ ok: false, text: typeof detail === 'string' ? detail : (e?.message || 'Release failed') });
                  } finally {
                    setBusy(false);
                  }
                }}>
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Release from baseline
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={closeDialog} disabled={busy}>Cancel</Button>
            <Button size="sm" onClick={run}
              disabled={busy || reason.trim().length < 5 || (swapMode && !swapTarget)}>
              {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
              {readOnly ? 'Send request to Finance' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BudgetWorkbench;
